import mapboxgl, {
  Map,
  Style,
  CameraOptions,
  LngLatBoundsLike,
  MapboxOptions,
  AnySourceImpl,
  AnySourceData,
  AnyLayer,
  Sources,
  GeoJSONSource,
  Expression,
  VectorSource,
  LineLayer,
} from "mapbox-gl";
import { createContext, Dispatch, SetStateAction } from "react";
import { Feature, Polygon } from "geojson";
import {
  ArcgisFeatureLayerFetchStrategy,
  ArchivedSourceFragment,
  BasemapDetailsFragment,
  BasemapType,
  ClientOverlayDataTableFragment,
  DataLayerDetailsFragment,
  DataSourceDetailsFragment,
  DataSourceTypes,
  InteractivityType,
  MapBookmarkDetailsFragment,
  OptionalBasemapLayersGroupType,
  OverlayFragment,
  SketchPresentFragmentDoc,
  SpriteDetailsFragment,
} from "../generated/graphql";
import {
  extractTilesUuidFromUrl,
  hostedStyleUrl,
  isHostedDataHost,
  withHostedAuthParams,
} from "./tilesAuth";
import {
  fetchGlStyle,
  hasMapboxAccessToken,
  MissingMapboxAccessTokenError,
  shouldAttemptMapboxStyleFallback,
  styleReferencesMapbox,
} from "../useMapboxStyle";
import LayerInteractivityManager from "./LayerInteractivityManager";
import bytes from "bytes";
import bbox from "@turf/bbox";
import ServiceWorkerWindow from "../offline/ServiceWorkerWindow";
import { OfflineTileSettings } from "../offline/OfflineTileSettings";
import md5 from "md5";
import LRUCache from "mnemonist/lru-cache";
import debounce from "lodash.debounce";
import { currentSidebarState } from "../projects/ProjectAppSidebar";
import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { compileLegendFromGLStyleLayers } from "./legends/compileLegend";
import {
  DataTableAggregation,
  DataTableFeatureSeriesPoint,
  dataTableFilterLabel,
  hiddenDataTableFilterColumns,
  omitFiltersForColumns,
  parseFilterColumnLabels,
  replicateQueryFields,
  partitionDataTableFilters,
  resolveDataTableVisualizationSettings,
} from "./dataTableQueryApi";
import {
  DataTableStatesMap,
  LayerDataTableState,
  buildDataTableStatesFromLayers,
  exclusiveDataTableStates,
  layerStatesForPreferences,
} from "./dataTableLayerState";
import {
  RememberedDataTableSettings,
  lookupDataTableSettings,
  parseDataTableSettingsMemory,
  resolveLayerDataTableChange,
} from "./dataTableSettingsMemory";
import { mergeOrganismFilter } from "./overlayTaxonSearch";
import { TemporalClock } from "@seasketch/geostats-types";
import {
  DATA_TABLE_ACTIVE_COLOR,
  DATA_TABLE_CIRCLE_FILL_OPACITY,
  DATA_TABLE_CIRCLE_STROKE_OPACITY,
  DATA_TABLE_LOADING_COLOR,
  DATA_TABLE_LOADING_FILL_OPACITY,
  DATA_TABLE_LOADING_STROKE_OPACITY,
  DATA_TABLE_NO_DATA_FILL_OPACITY,
  DATA_TABLE_NO_DATA_STROKE_COLOR,
  DATA_TABLE_NO_DATA_STROKE_OPACITY,
  DATA_TABLE_NO_DATA_STROKE_WIDTH,
  DATA_TABLE_NO_DATA_VALUE,
  DATA_TABLE_PAINT_TRANSITION,
  DATA_TABLE_VALUE_PROPERTY,
  DATA_TABLE_ZERO_FILL_OPACITY,
  DATA_TABLE_ZERO_STROKE_WIDTH,
  DATA_TABLE_NO_DATA_COLOR,
  DATA_TABLE_HOVER_FILL_OPACITY,
  DATA_TABLE_HOVER_STROKE_WIDTH,
  buildDataTableCircleColorExpression,
  buildDataTableCircleRadiusExpression,
  buildDataTableCircleStrokeColorExpression,
  buildDataTableValueExpression,
  dataTableHoveredExpression,
  dataTableIsZeroExpression,
} from "./dataTableMapStyle";
import {
  columnStatsUrlForTable,
  fetchDataTableColumnStats,
  getCachedDataTableColumnStats,
} from "./useDataTableColumnStats";
import { DataTablesColumnStats, isTemporalInfo } from "@seasketch/geostats-types";
import { LegendItem } from "./Legend";
import { EventEmitter } from "eventemitter3";
import cloneDeep from "lodash.clonedeep";
import {
  ArcGISDynamicMapService,
  ArcGISFeatureLayerSource,
  ArcGISRESTServiceRequestManager,
  ArcGISTiledMapService,
  CustomGLSource,
} from "@seasketch/mapbox-gl-esri-sources";
import { OrderedLayerSettings } from "@seasketch/mapbox-gl-esri-sources/dist/src/CustomGLSource";
import { isArcGISDynamicMapService } from "@seasketch/mapbox-gl-esri-sources/dist/src/ArcGISDynamicMapService";
import { isArcgisFeatureLayerSource } from "@seasketch/mapbox-gl-esri-sources/dist/src/ArcGISFeatureLayerSource";
import { addInteractivityExpressions } from "../admin/data/glStyleUtils";
import { createBoundsRecursive } from "../projects/OverlayLayers";
import { TocMenuItemType } from "../admin/data/TableOfContentsItemMenu";
import { isExpression } from "./legends/utils";
import {
  buildInaturalistSourcesAndLayers,
  normalizeInaturalistParams,
} from "./inaturalist";
import {
  BasemapContextState,
  OptionalBasemapLayerValue,
} from "./BasemapContext";
import { LayerStateManager } from "./LayerStateManager";
import {
  DataTableQueryManager,
  ResolvedDataTableVisualizationSettings,
} from "./DataTableQueryManager";
import {
  applyClockPaintHidden,
  canKeepTilesWhenClockHidden,
  snapClockOpacityTransitions,
} from "./mapTemporalStyle";

export type {
  LayerDataTableState,
  DataTableStatesMap,
} from "./dataTableLayerState";

export const MeasureEventTypes = {
  Started: "measure_started",
  Stopped: "measure_stopped",
};

const rejectAfter = (duration: number) =>
  new Promise((resolve, reject) => {
    setTimeout(() => {
      reject(new Error("Timeout"));
    }, duration);
  });

export const withTimeout = (
  duration: number,
  callback: (...args: any) => Promise<any>
) => {
  return (...args: any) => {
    return Promise.race([rejectAfter(duration), callback(...args)]);
  };
};

const graphqlURL = new URL(
  process.env.REACT_APP_GRAPHQL_ENDPOINT || "http://localhost:3857/graphql"
);

const STALE_CUSTOM_SOURCE_SIZE = 3;

export const BASE_SERVER_ENDPOINT = `${graphqlURL.protocol}//${graphqlURL.host}`;

const LocalSketchGeometryCache = new LRUCache<
  number,
  { timestamp: string; feature: Feature<any> }
>(10);

/**
 * Multiple "digitizing" tools active at once would compete for the same cursor
 * events and conflict with each other. This enum is used to track the state of
 * the digitizing tools and prevent conflicts.
 */
export enum DigitizingLockState {
  /**
   * No digitizing tools are active. Popups and other interactivity is enabled.
   */
  Free,
  /**
   * A digitizing tool is active and has locked the map. Popups and other
   * interactivity is disabled. This is the most active state, with the
   * mousemove events directly moving a "cursor" vertex until it is dropped.
   */
  CursorActive,
  /**
   * A digitizing tool is partially active, with a geometry ready to be edited
   * when the user drags a vertex. Popups and other interactivity may be
   * enabled, though this would require careful coordination with the digitizing
   * tool.
   */
  Editing,
}

export const DigitizingLockStateChangeEventType =
  "DigitizingLockStateChangeEvent";

export type DigitizingLockStateChangeEventPayload = {
  digitizingLockState: DigitizingLockState;
  digitizingLockedBy: string | undefined;
};

// TODO: we're not using project settings for this yet
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN!;

export interface LayerState {
  visible: boolean;
  opacity?: number;
  zOrderOverride?: number;
  loading: boolean;
  error?: Error;
  /**
   * If true, it means that while the layer may be "visible" as selected from
   * the table of contents, the user may have temporarily hidden it in the
   * legend.
   */
  hidden?: boolean;
  /**
   * User intent for an activated overlay data table on this layer.
   * Identified by overlay_data_tables.stable_id. Persisted with map prefs
   * and map bookmarks.
   */
  dataTable?: LayerDataTableState;
}

export interface SketchClassLayerState extends LayerState {
  sketchClassId: number;
  name: string;
  zOrderOverride?: number;
  legendItem: LegendItem;
  styles: any[];
}

export interface SketchLayerState extends LayerState {
  sketchClassId?: number;
  filterMvtUrl?: string;
}
/**
 * Internal state type. Extends MapContextInterface with
 * sketchClassLayerStates (managed directly on internalState) and
 * sketchLayerStates (present from initial construction but kept
 * current via the stateChanged event from sketchStates, not via setState).
 */
type FullInternalState = MapContextInterface & SketchLayerContextState;

class MapContextManager extends EventEmitter {
  map?: Map;
  private preferencesKey?: string;
  /** Last-used column/op/filters keyed by table stableId (LRU, persisted). */
  private dataTableSettingsMemory: RememberedDataTableSettings[] = [];
  private clientDataSources: {
    [dataSourceId: string]: DataSourceDetailsFragment;
  } = {};
  private layers: {
    [tocStableId: string]: {
      tocId: string;
    } & DataLayerDetailsFragment;
  } = {};
  /** Manages overlay layer state (keyed by stableId). */
  private overlayStates: LayerStateManager<LayerState>;
  /** Manages per-sketch layer state (keyed by string sketch id). */
  private sketchStates: LayerStateManager<SketchLayerState>;
  private basemaps: { [id: string]: BasemapDetailsFragment } = {};
  private _setManagerState?: Dispatch<SetStateAction<MapManagerState>>;
  private _setSketchLayerState?: Dispatch<
    SetStateAction<SketchLayerContextState>
  >;
  private _setMapOverlayState?: Dispatch<
    SetStateAction<MapOverlayContextState>
  >;
  private _setLegendsState?: Dispatch<SetStateAction<LegendsContextState>>;
  /** Basemap state from BasemapContext, set via updateBasemapState by MapManagerContextProvider */
  private basemapState: BasemapContextState | null = null;
  /**
   * Cached query results for activated data tables (keyed by TOC stableId).
   * User intent lives on LayerState.dataTable; query params are resolved on
   * demand from the TOC catalog + admin constraints.
   */
  private activatedDataTableValues: {
    [tocStableId: string]: {
      queryKey: string;
      min: number;
      max: number;
      scaleMin: number;
      scaleMax: number;
      hasZero: boolean;
      values: { [featureId: string]: number };
    };
  } = {};
  private activatedDataTableAppliedFeatureIds: {
    [tocStableId: string]: Set<string> | undefined;
  } = {};
  private activatedDataTableRequests: {
    [tocStableId: string]: string | undefined;
  } = {};
  /** True while a data-table query is in flight for a layer. Drives gray/faded paint. */
  private activatedDataTableLoading: {
    [tocStableId: string]: boolean | undefined;
  } = {};
  private activatedDataTableSourceListeners: {
    [tocStableId: string]: (event: mapboxgl.MapSourceDataEvent) => void;
  } = {};
  private _ready = false;

  /** Cached result of computeInatCtas for referential stability. */
  private _lastInatCtas: { projectId: string; label?: string }[] = [];

  private initialCameraOptions?: CameraOptions;
  private initialBounds?: LngLatBoundsLike;
  private internalState: FullInternalState;
  private mapIsLoaded = false;
  private mapContainer?: HTMLDivElement;
  private basemapsWereSet = false;
  private userAccessToken?: string | null;
  private mapAccessToken?: string | null;
  /**
   * Authoritative list from Project.hostedTileUuidsRequiringAuth.
   * `null` until ProjectMetadata arrives — fall back conservatively (attach
   * token to any hosted UUID when a mapAccessToken is available).
   */
  private hostedTileUuidsRequiringAuth: Set<string> | null = null;
  private editableSketchId?: number;
  private selectedSketches?: number[];
  private sketchTimestamps = new global.Map<number, string>();
  private hideEditableSketchId?: number;
  private arcgisRequestManager: ArcGISRESTServiceRequestManager =
    new ArcGISRESTServiceRequestManager();
  // Used to track previous map state before the application of a map bookmark
  // so that the map state change may be undone.
  private previousMapState:
    | undefined
    | Pick<
        MapBookmarkDetailsFragment,
        | "cameraOptions"
        | "basemapOptionalLayerStates"
        | "visibleDataLayers"
        | "selectedBasemap"
        | "dataTableStates"
      > = undefined;
  /**
   * The manager needs an up-to-date list of geoprocessing reference ids so that
   * clients can toggle the appropriate TableOfContentsItem. The manager will
   * keep this list up to date whenever reset() is called, but it should also
   * be updated by the client using setGeoprocessingReferenceId() if it is
   * changed.
   */
  private geoprocessingReferenceIds: { [referenceId: string]: string } = {};
  private customSources: {
    [sourceId: number]: {
      customSource: CustomGLSource<any>;
      visible: boolean;
      lastUsedTimestamp: number;
      listenersAdded: boolean;
      sublayers?: OrderedLayerSettings;
    };
  } = {};

  private dataTableQueryManager: DataTableQueryManager;
  /** tocStableIds with LayerState.dataTable intent — keeps sync O(active). */
  private dataTableActiveTocIds = new Set<string>();
  /**
   * Overlay TOC stableIds hidden by the map clock. Independent of
   * LayerState.hidden (legend eye) and LayerState.visible (TOC checkbox).
   */
  private temporallyHiddenTocIds = new Set<string>();
  /** Dedupes stacked `idle` listeners for data-table feature-state sync. */
  private dataTableFeatureStateSyncIdleQueued = false;
  private dataTableClockRaf: number | null = null;

  constructor(
    initialState: MapContextInterface,
    initialSketchState: SketchLayerContextState,
    setManagerState: Dispatch<SetStateAction<MapManagerState>>,
    setSketchLayerState: Dispatch<SetStateAction<SketchLayerContextState>>,
    setMapOverlayState: Dispatch<SetStateAction<MapOverlayContextState>>,
    setLegendsState: Dispatch<SetStateAction<LegendsContextState>>,
    initialCameraOptions?: CameraOptions,
    preferencesKey?: string,
    cacheSize?: number,
    initialBounds?: LngLatBoundsLike
  ) {
    super();
    cacheSize = cacheSize || bytes("50mb");
    this._setManagerState = setManagerState;
    this._setSketchLayerState = setSketchLayerState;
    this._setMapOverlayState = setMapOverlayState;
    this._setLegendsState = setLegendsState;
    // @ts-ignore
    window.mapContext = this;
    this.preferencesKey = preferencesKey;
    this.dataTableSettingsMemory = this.loadDataTableSettingsMemory();
    this.internalState = { ...initialState, ...initialSketchState };
    this.initialCameraOptions = initialCameraOptions;
    this.initialBounds = initialBounds;
    this.overlayStates = new LayerStateManager<LayerState>(
      "overlay",
      initialState.layerStatesByTocStaticId
    );
    this.sketchStates = new LayerStateManager<SketchLayerState>(
      "sketch",
      Object.fromEntries(
        Object.entries(initialSketchState.sketchLayerStates).map(([k, v]) => [
          k.toString(),
          v,
        ])
      )
    );

    // When effective state changes, push to the relevant React context(s).
    // Overlay and sketch managers have separate handlers so that overlay
    // loading events don't needlessly churn the SketchLayerContext (and
    // vice-versa).
    this.overlayStates.on("stateChanged", this.onOverlayStateChanged);
    this.sketchStates.on("stateChanged", this.onSketchStateChanged);
    this.dataTableQueryManager = new DataTableQueryManager(
      this.mapAccessToken ?? null
    );
    this.dataTableQueryManager.setOnLegendSummaryChange(() => {
      this.updateLegends();
    });
    // Seed from restored preferences / initial layer states.
    for (const tocStableId of this.overlayStates.keys()) {
      const restored = this.overlayStates.getRaw(tocStableId)?.dataTable;
      if (restored?.stableId) {
        this.dataTableActiveTocIds.add(tocStableId);
        this.commitDataTableSettingsMemory(
          resolveLayerDataTableChange(undefined, restored, this.dataTableSettingsMemory)
            .memory
        );
      }
    }
    this.deactivateOtherDataTables();
  }

  private loadDataTableSettingsMemory(): RememberedDataTableSettings[] {
    if (!this.preferencesKey || typeof window === "undefined") {
      return [];
    }
    try {
      const raw = window.localStorage.getItem(this.preferencesKey);
      if (!raw) {
        return [];
      }
      const prefs = JSON.parse(raw);
      return parseDataTableSettingsMemory(prefs?.dataTableSettings);
    } catch {
      return [];
    }
  }

  private commitDataTableSettingsMemory(
    memory: RememberedDataTableSettings[]
  ) {
    this.dataTableSettingsMemory = memory;
  }

  getCustomGLSource(sourceId: number) {
    return this.customSources[sourceId]?.customSource;
  }

  /**
   * Update basemap state from BasemapContext. Called by MapManagerContextProvider.
   */
  updateBasemapState(state: BasemapContextState) {
    this.basemapState = state;
    // Sync the basemaps lookup for bookmark/style code
    this.basemaps = {};
    for (const b of state.basemaps) {
      this.basemaps[b.id.toString()] = b;
    }
    // Mark ready when basemaps are set
    if (state.ready && state.basemaps.length > 0 && !this.basemapsWereSet) {
      this.basemapsWereSet = true;
      this._ready =
        !!(this.initialCameraOptions || this.initialBounds) &&
        this.basemapsWereSet;
      this._setManagerState?.((prev) => ({ ...prev, ready: this._ready }));
    }
  }

  /**
   * Set or clear data-table user intent on a layer. Source of truth is
   * LayerState.dataTable (persisted with prefs / bookmarks). Switching
   * tables restores that table's last column/op/filters from memory.
   * Only one overlay may visualize a table at a time — activating a table
   * clears others so they return to their default map style.
   */
  setLayerDataTable(tocStableId: string, state: LayerDataTableState | null) {
    if (!this.overlayStates.has(tocStableId)) {
      return;
    }
    const previous = this.overlayStates.getRaw(tocStableId)?.dataTable;
    const previousTableId = previous?.stableId;
    const resolved = resolveLayerDataTableChange(
      previous,
      state,
      this.dataTableSettingsMemory
    );
    this.commitDataTableSettingsMemory(resolved.memory);
    const next = resolved.next;
    if (next === null || !next.stableId) {
      this.forgetLayerDataTable(tocStableId, previousTableId);
    } else {
      this.deactivateOtherDataTables(tocStableId);
      this.overlayStates.patch(
        tocStableId,
        {
          dataTable: { ...next },
        },
        true
      );
      this.dataTableActiveTocIds.add(tocStableId);
      if (previousTableId && previousTableId !== next.stableId) {
        this.dataTableQueryManager.clearSeriesForTable(previousTableId);
      }
    }
    this.scheduleDataTableFeatureStateSync();
    this.debouncedUpdateStyle();
    this.debouncedUpdatePreferences();
    this.updateLegends();
    if (next?.stableId && next.stableId !== previousTableId) {
      this.emit("dataTableActivated", {
        tocStableId,
        stableId: next.stableId,
        switchedFromTable: Boolean(previousTableId),
      });
    }
  }

  /**
   * Show a layer and activate one of its data tables filtered to a single
   * organism value. Keeps that table's last measure column and aggregation.
   */
  activateDataTableForTaxon(
    tocStableId: string,
    tableStableId: string,
    organismColumn: string,
    value: string
  ) {
    const remembered = lookupDataTableSettings(
      this.dataTableSettingsMemory,
      tableStableId
    );
    const merged = mergeOrganismFilter(remembered, organismColumn, value);
    this.showTocItems([tocStableId]);
    this.setLayerDataTable(tocStableId, {
      stableId: tableStableId,
      column: merged.column,
      op: merged.op,
      filters: merged.filters,
    });
    this.showHiddenLayer(tocStableId);
  }

  /**
   * Drop data-table visualization from a layer so its default GL style
   * (for example a site list) is used on the next style update.
   */
  private forgetLayerDataTable(
    tocStableId: string,
    previousTableId?: string
  ) {
    this.overlayStates.patch(tocStableId, { dataTable: undefined }, true);
    this.dataTableActiveTocIds.delete(tocStableId);
    const layer = this.layers[tocStableId];
    if (layer) {
      this.dataTableQueryManager.clearLegendSummary(
        this.overlayStates.prefixedSourceId(layer.dataSourceId)
      );
    }
    if (previousTableId) {
      this.dataTableQueryManager.clearSeriesForTable(previousTableId);
    }
  }

  /**
   * Turn off every active data-table visualization except `keepTocStableId`.
   * When omitted, keeps the last remaining active layer.
   */
  private deactivateOtherDataTables(keepTocStableId?: string) {
    const active = [...this.dataTableActiveTocIds];
    const keep =
      keepTocStableId ??
      (active.length > 0 ? active[active.length - 1] : undefined);
    for (const tocStableId of active) {
      if (tocStableId === keep) {
        continue;
      }
      const previous = this.overlayStates.getRaw(tocStableId)?.dataTable;
      if (previous?.stableId) {
        this.commitDataTableSettingsMemory(
          resolveLayerDataTableChange(
            previous,
            null,
            this.dataTableSettingsMemory
          ).memory
        );
      }
      this.forgetLayerDataTable(tocStableId, previous?.stableId);
    }
  }

  /**
   * Latest data-table intent from overlay state (not the possibly-stale
   * React context snapshot). Use this when reconciling required filters so
   * an in-flight legend effect cannot overwrite a newer user selection.
   */
  getLayerDataTable(tocStableId: string): LayerDataTableState | undefined {
    return this.overlayStates.getRaw(tocStableId)?.dataTable;
  }

  /**
   * Apply a bookmark-style dataTableStates map onto current overlay layers.
   * Clears dataTable only on layers that currently have one and are absent
   * from the bookmark map; applies only keys present in dataTableStates.
   */
  applyDataTableStates(dataTableStates: DataTableStatesMap | null | undefined) {
    const states = exclusiveDataTableStates(dataTableStates);
    // Bookmark apply must turn off data tables that aren't in the map.
    for (const tocStableId of [...this.dataTableActiveTocIds]) {
      if (!states[tocStableId]?.stableId) {
        const previous = this.overlayStates.getRaw(tocStableId)?.dataTable;
        if (previous?.stableId) {
          this.commitDataTableSettingsMemory(
            resolveLayerDataTableChange(
              previous,
              null,
              this.dataTableSettingsMemory
            ).memory
          );
        }
        this.forgetLayerDataTable(tocStableId, previous?.stableId);
      }
    }
    for (const [tocStableId, next] of Object.entries(states)) {
      if (!next?.stableId || !this.overlayStates.has(tocStableId)) {
        continue;
      }
      const previous = this.overlayStates.getRaw(tocStableId)?.dataTable;
      const resolved = resolveLayerDataTableChange(
        previous,
        next,
        this.dataTableSettingsMemory
      );
      this.commitDataTableSettingsMemory(resolved.memory);
      this.overlayStates.patch(
        tocStableId,
        {
          dataTable: { ...(resolved.next || next) },
        },
        true
      );
      this.dataTableActiveTocIds.add(tocStableId);
    }
    this.scheduleDataTableFeatureStateSync();
    this.debouncedUpdateStyle();
    this.debouncedUpdatePreferences();
    this.updateLegends();
  }

  /**
   * Idempotent apply path plus a follow-up on the next map `idle`.
   *
   * Important: if the map is *already* idle when we register `once("idle")`,
   * Mapbox will not fire until it becomes busy again — so we also sync
   * immediately and once more after the next paint when the style is loaded.
   * That covers reload races where the TOC catalog / sources arrive after the
   * first style settle, and style-hash early returns that never call setStyle.
   */
  private scheduleDataTableFeatureStateSync() {
    this.syncDataTableFeatureStates();
    if (!this.map) {
      return;
    }
    const map = this.map;
    if (!this.dataTableFeatureStateSyncIdleQueued) {
      this.dataTableFeatureStateSyncIdleQueued = true;
      map.once("idle", () => {
        this.dataTableFeatureStateSyncIdleQueued = false;
        if (this.map === map) {
          this.syncDataTableFeatureStates();
        }
      });
    }
    // Already-idle maps never re-emit `idle` until something else dirties
    // them. Retry once the current frame finishes if the style is ready.
    requestAnimationFrame(() => {
      if (this.map !== map || !map.isStyleLoaded()) {
        return;
      }
      this.syncDataTableFeatureStates();
    });
  }

  /**
   * Active data-table proportional-symbol layers for hover value tooltips.
   * Consumed by LayerInteractivityManager via MapUIContext.
   */
  getDataTableInteractiveLayers(): {
    glLayerId: string;
    sourceId: string;
    sourceLayer?: string;
    column: string;
    op: string;
    columnLabel: string;
    tableName?: string;
    layerTitle?: string;
    getSeries?: (featureId: string) => {
      points: DataTableFeatureSeriesPoint[];
      currentSteps: string[];
    } | null;
    getRangeLabel?: () => string | undefined;
  }[] {
    const out: {
      glLayerId: string;
      sourceId: string;
      sourceLayer?: string;
      column: string;
      op: string;
      columnLabel: string;
      tableName?: string;
      layerTitle?: string;
      getSeries?: (featureId: string) => {
        points: DataTableFeatureSeriesPoint[];
        currentSteps: string[];
      } | null;
      getRangeLabel?: () => string | undefined;
    }[] = [];
    for (const tocStableId of this.dataTableActiveTocIds) {
      const overlayState = this.overlayStates.getRaw(tocStableId);
      if (
        !overlayState?.dataTable?.stableId ||
        !overlayState.visible ||
        overlayState.hidden ||
        this.temporallyHiddenTocIds.has(tocStableId)
      ) {
        continue;
      }
      const activation =
        this.resolveDataTableVisualizationSettings(tocStableId);
      if (!activation?.query.op) {
        continue;
      }
      const op = Array.isArray(activation.query.op)
        ? activation.query.op[0]
        : activation.query.op;
      if (!activation.query.column && op !== "count") {
        continue;
      }
      const layer = this.layers[tocStableId];
      if (!layer) {
        continue;
      }
      const sourceLayer =
        this.archivedSource && this.archivedSource.dataLayerId === layer.id
          ? this.archivedSource.sourceLayer || undefined
          : layer.sourceLayer || undefined;
      const columnLabels = parseFilterColumnLabels(
        activation.table.filterColumnLabels
      );
      const column = activation.query.column;
      out.push({
        glLayerId: idForLayer(layer, 0),
        sourceId: this.overlayStates.prefixedSourceId(layer.dataSourceId),
        sourceLayer,
        column: column || "",
        op,
        columnLabel: column
          ? dataTableFilterLabel(column, columnLabels)
          : "",
        tableName: activation.table.name || undefined,
        layerTitle: this.tocItems[tocStableId]?.label,
        getSeries: (featureId: string) =>
          this.dataTableQueryManager.getFeatureSeries(activation, featureId),
        getRangeLabel: () => this.dataTableQueryManager.getTooltipRangeLabel(),
      });
    }
    return out;
  }

  /**
   * Drive data-table queries / feature-state from LayerState.dataTable.
   * Style computation stays pure — this is the side-effect path.
   *
   * Only iterates layers with active data-table intent (not the full TOC).
   * After style updates, reconciles applied markers against the live map:
   * style diffs often keep feature-state on reused sources; only rebuilds
   * that drop state (or missing sources) trigger a re-fetch via browser cache.
   */
  private syncDataTableFeatureStates() {
    if (!this.map || this.dataTableActiveTocIds.size === 0) {
      return;
    }
    for (const tocStableId of this.dataTableActiveTocIds) {
      if (!this.overlayStates.getRaw(tocStableId)?.dataTable?.stableId) {
        this.dataTableActiveTocIds.delete(tocStableId);
        continue;
      }
      const activation =
        this.resolveDataTableVisualizationSettings(tocStableId);
      if (
        !activation?.query.column ||
        !activation.query.op ||
        !activation.table.joinColumn ||
        !activation.table.queryUrl
      ) {
        continue;
      }
      const layer = this.layers[tocStableId];
      if (!layer) {
        continue;
      }
      const sourceId = this.overlayStates.prefixedSourceId(layer.dataSourceId);
      const sourceLayer =
        this.archivedSource && this.archivedSource.dataLayerId === layer.id
          ? this.archivedSource.sourceLayer || undefined
          : layer.sourceLayer || undefined;
      // Drop stale "applied" markers when the source/state didn't survive.
      this.dataTableQueryManager.reconcileAppliedState(sourceId, sourceLayer);
      if (!this.map.getSource(sourceId)) {
        // Source not on the map yet (style still building). A scheduled
        // idle / rAF retry will pick this up once sources exist.
        continue;
      }
      const uuid = extractTilesUuidFromUrl(activation.table.queryUrl);
      const requiresToken = uuid
        ? this.hostedUuidNeedsMapAccessToken(uuid)
        : false;
      void this.dataTableQueryManager.applyFeatureState(
        sourceId,
        sourceLayer,
        activation,
        requiresToken
      );
    }
  }

  /**
   * Public access to the resolved data-table visualization for a layer —
   * the same settings object driving map paint. Used by the QA/QC
   * "Show rows in calculation" modal so its display of op/column/filters
   * cannot drift from what the map actually queried.
   */
  getDataTableCalculationSettings(
    tocStableId: string
  ): ResolvedDataTableVisualizationSettings | undefined {
    return this.resolveDataTableVisualizationSettings(tocStableId);
  }

  /**
   * Engine-computed per-step series for one join key (site), from the same
   * cached query result that paints the map / hover sparkline.
   */
  getDataTableFeatureSeries(tocStableId: string, featureId: string) {
    const settings = this.resolveDataTableVisualizationSettings(tocStableId);
    if (!settings) {
      return null;
    }
    return this.dataTableQueryManager.getFeatureSeries(settings, featureId);
  }

  /** Currently displayed map value for one join key (see DataTableQueryManager). */
  getDataTableFeatureCurrentValue(tocStableId: string, featureId: string) {
    const settings = this.resolveDataTableVisualizationSettings(tocStableId);
    if (!settings) {
      return undefined;
    }
    return this.dataTableQueryManager.getFeatureCurrentValue(
      settings,
      featureId
    );
  }

  /**
   * Raw rows behind the map statistic for one join key, using the exact
   * clock/filter parameters of the active visualization. See
   * DataTableQueryManager.fetchCalculationRows for the coupling guarantee.
   */
  async fetchDataTableCalculationRows(
    tocStableId: string,
    featureId: string,
    signal?: AbortSignal,
    whenOverride?: { start: number; end: number } | null
  ) {
    const settings = this.resolveDataTableVisualizationSettings(tocStableId);
    if (!settings?.table.queryUrl) {
      throw new Error("No active data table visualization for this layer");
    }
    const uuid = extractTilesUuidFromUrl(settings.table.queryUrl);
    const requiresToken = uuid
      ? this.hostedUuidNeedsMapAccessToken(uuid)
      : false;
    return this.dataTableQueryManager.fetchCalculationRows(
      settings,
      featureId,
      requiresToken,
      signal,
      whenOverride
    );
  }

  /**
   * The engine's replicate-by-replicate account of one feature's number.
   * See DataTableQueryManager.fetchCalculationAudit.
   */
  async fetchDataTableCalculationAudit(
    tocStableId: string,
    featureId: string,
    signal?: AbortSignal,
    whenOverride?: { start: number; end: number } | null
  ) {
    const settings = this.resolveDataTableVisualizationSettings(tocStableId);
    if (!settings?.table.queryUrl) {
      throw new Error("No active data table visualization for this layer");
    }
    const uuid = extractTilesUuidFromUrl(settings.table.queryUrl);
    const requiresToken = uuid
      ? this.hostedUuidNeedsMapAccessToken(uuid)
      : false;
    return this.dataTableQueryManager.fetchCalculationAudit(
      settings,
      featureId,
      requiresToken,
      signal,
      whenOverride
    );
  }

  /**
   * Resolve LayerState.dataTable against the TOC catalog + admin constraints.
   * Returns undefined when intent is missing, the catalog isn't ready, or the
   * stableId no longer exists. Does not mutate LayerState.
   */
  private resolveDataTableVisualizationSettings(
    tocStableId: string
  ): ResolvedDataTableVisualizationSettings | undefined {
    const dataTable = this.overlayStates.getRaw(tocStableId)?.dataTable;
    if (!dataTable?.stableId) {
      return undefined;
    }
    const tables = this.tocItems[tocStableId]?.overlayDataTables;
    if (!tables) {
      return undefined;
    }
    const table = tables.find((entry) => entry.stableId === dataTable.stableId);
    if (!table) {
      return undefined;
    }
    const resolved = resolveDataTableVisualizationSettings(table, {
      column: dataTable.column,
      op: dataTable.op,
      filters: dataTable.filters,
    });
    const partitioned = partitionDataTableFilters(
      omitFiltersForColumns(
        resolved.filters,
        [
          ...hiddenDataTableFilterColumns(table),
          table.joinColumn,
        ].filter(Boolean)
      ),
      table,
      table.joinColumn
    );
    return {
      table,
      query: {
        column: resolved.column,
        op: resolved.op,
        ...replicateQueryFields(table, resolved.column),
        ...partitioned,
        joinColumn: table.joinColumn,
      },
    };
  }

  private removeActivatedDataTableSourceListener(tocStableId: string) {
    const listener = this.activatedDataTableSourceListeners[tocStableId];
    if (listener && this.map) {
      this.map.off("sourcedata", listener);
    }
    delete this.activatedDataTableSourceListeners[tocStableId];
  }

  /**
   * Promoted feature IDs for a data-table join come from column-stats for the
   * table join column (same values used as Mapbox promoteId). Not from
   * currently loaded tiles, and not from overlay geostats.
   */
  private getJoinFeatureIdsFromColumnStats(
    columnStats: DataTablesColumnStats | undefined,
    joinColumn: string
  ): string[] {
    if (!columnStats?.columns?.length || !joinColumn) {
      return [];
    }
    const attribute = columnStats.columns.find(
      (column) => column.attribute === joinColumn
    );
    if (!attribute?.values) {
      return [];
    }
    return Object.keys(attribute.values);
  }

  private scheduleActivatedDataTableFeatureStateApply(tocStableId: string) {
    if (!this.map) {
      return;
    }
    const cached = this.activatedDataTableValues[tocStableId];
    const activation = this.resolveDataTableVisualizationSettings(tocStableId);
    if (!cached?.values || !activation) {
      return;
    }
    const layer = this.layers[tocStableId];
    const table = activation.table;
    if (!layer || !table.overlayJoinColumn || !table.joinColumn) {
      return;
    }
    const sourceId = this.overlayStates.prefixedSourceId(layer.dataSourceId);
    const columnStatsUrl = columnStatsUrlForTable(table);
    const queryKeyAtStart = cached.queryKey;
    const applyWithColumnStats = async () => {
      if (!this.map) {
        return;
      }
      const currentValues = this.activatedDataTableValues[tocStableId]?.values;
      const currentLayer = this.layers[tocStableId];
      const currentActivation =
        this.resolveDataTableVisualizationSettings(tocStableId);
      if (
        !currentValues ||
        !currentLayer ||
        !currentActivation?.table.overlayJoinColumn ||
        !currentActivation.table.joinColumn
      ) {
        return;
      }
      let columnStats = columnStatsUrl
        ? getCachedDataTableColumnStats(columnStatsUrl)
        : undefined;
      if (!columnStats && columnStatsUrl) {
        try {
          columnStats = await fetchDataTableColumnStats(
            columnStatsUrl,
            this.mapAccessToken
          );
        } catch (error) {
          console.error(error);
        }
      }
      // Re-check after the async column-stats fetch; activation may have changed.
      if (
        this.activatedDataTableValues[tocStableId]?.values !== currentValues ||
        this.activatedDataTableValues[tocStableId]?.queryKey !== queryKeyAtStart
      ) {
        return;
      }
      const previousIds =
        this.activatedDataTableAppliedFeatureIds[tocStableId] ||
        new Set<string>();
      const joinIds = this.getJoinFeatureIdsFromColumnStats(
        columnStats,
        currentActivation.table.joinColumn
      );
      // Prefer the complete join-column ID set from column-stats. Fall back to
      // query result keys only if column-stats is missing.
      const allFeatureIds =
        joinIds.length > 0 ? joinIds : Object.keys(currentValues);
      const nextIds = new Set<string>();
      for (const featureId of allFeatureIds) {
        const stateValue =
          currentValues[featureId] !== undefined
            ? currentValues[featureId]
            : DATA_TABLE_NO_DATA_VALUE;
        this.map.setFeatureState(
          {
            source: sourceId,
            id: featureId,
            ...(currentLayer.sourceLayer
              ? { sourceLayer: currentLayer.sourceLayer }
              : {}),
          },
          { [DATA_TABLE_VALUE_PROPERTY]: stateValue }
        );
        nextIds.add(featureId);
      }
      for (const featureId of previousIds) {
        if (!nextIds.has(featureId)) {
          this.map.removeFeatureState(
            {
              source: sourceId,
              id: featureId,
              ...(currentLayer.sourceLayer
                ? { sourceLayer: currentLayer.sourceLayer }
                : {}),
            },
            DATA_TABLE_VALUE_PROPERTY
          );
        }
      }
      this.activatedDataTableAppliedFeatureIds[tocStableId] = nextIds;
    };
    // Apply once when the source exists. Do not re-apply on sourcedata /
    // pan / zoom — feature-state is keyed by id and survives tile unload.
    if (this.map.getSource(sourceId)) {
      void applyWithColumnStats();
      return;
    }
    if (this.activatedDataTableSourceListeners[tocStableId]) {
      return;
    }
    const listener = (event: mapboxgl.MapSourceDataEvent) => {
      if (event.sourceId !== sourceId || !event.isSourceLoaded) {
        return;
      }
      this.removeActivatedDataTableSourceListener(tocStableId);
      void applyWithColumnStats();
    };
    this.activatedDataTableSourceListeners[tocStableId] = listener;
    this.map.on("sourcedata", listener);
  }

  /**
   * Live map circle layer for an activated data table. ID and paint must stay
   * in sync with {@link setLayerOpacity} so the legend opacity slider can
   * call `setPaintProperty` on the same layer the style put on the map.
   */
  private buildDataTableProportionalSymbolLayer(
    layer: { tocId: string } & DataLayerDetailsFragment,
    sourceId: string,
    sourceLayer?: string
  ): AnyLayer {
    const valueExpression = ["feature-state", "scaledValue"] as Expression;
    // Unset feature-state is null; typeof !== "number" covers that and any
    // non-numeric sentinel in one check.
    const isNoData = [
      "!=",
      ["typeof", valueExpression],
      "number",
    ] as Expression;
    // Safe to compare numerically: case chains check isNoData before isZero.
    const isZero = dataTableIsZeroExpression();
    const isLoading = [
      "boolean",
      ["feature-state", "loading"],
      false,
    ] as Expression;
    const isHovered = dataTableHoveredExpression();
    return {
      id: idForLayer(layer, 0),
      type: "circle",
      source: sourceId,
      ...(sourceLayer ? { "source-layer": sourceLayer } : {}),
      metadata: {
        "s:data-table-proporional-symbol": true,
      },
      paint: {
        "circle-color": buildDataTableCircleColorExpression(
          isLoading,
          isNoData
        ),
        "circle-opacity": [
          "case",
          isLoading,
          DATA_TABLE_LOADING_FILL_OPACITY,
          isNoData,
          DATA_TABLE_NO_DATA_FILL_OPACITY,
          isZero,
          DATA_TABLE_ZERO_FILL_OPACITY,
          isHovered,
          DATA_TABLE_HOVER_FILL_OPACITY,
          DATA_TABLE_CIRCLE_FILL_OPACITY,
        ],
        "circle-stroke-opacity": [
          "case",
          isLoading,
          DATA_TABLE_LOADING_STROKE_OPACITY,
          isNoData,
          DATA_TABLE_NO_DATA_STROKE_OPACITY,
          1,
        ],
        "circle-stroke-color": buildDataTableCircleStrokeColorExpression(
          isNoData
        ),
        "circle-stroke-width": [
          "case",
          isNoData,
          DATA_TABLE_NO_DATA_STROKE_WIDTH,
          isZero,
          DATA_TABLE_ZERO_STROKE_WIDTH,
          isHovered,
          DATA_TABLE_HOVER_STROKE_WIDTH,
          0,
        ],
        "circle-radius": buildDataTableCircleRadiusExpression({
          valueExpression,
          scaleMin: 0,
          scaleMax: 1,
          zoomDependent: true,
          hideWhenMissing: false,
          isZero,
        }),
      },
    };
  }

  private dataTableCircleLayer(
    tocStableId: string,
    layer: { tocId: string } & DataLayerDetailsFragment,
    sourceId: string,
    sourceLayer?: string,
    legendOnly = false
  ): AnyLayer | undefined {
    const activation = this.resolveDataTableVisualizationSettings(tocStableId);
    if (!activation?.query.column || !activation.query.op) {
      return undefined;
    }
    // Note: query execution is orchestrated exclusively by
    // updateActivatedDataTableFeatureStates; style builders must stay pure.
    const values = this.activatedDataTableValues[tocStableId];
    const loading =
      !legendOnly && Boolean(this.activatedDataTableLoading[tocStableId]);
    const fillOpacity = loading
      ? DATA_TABLE_LOADING_FILL_OPACITY
      : DATA_TABLE_CIRCLE_FILL_OPACITY;
    const strokeOpacity = loading
      ? DATA_TABLE_LOADING_STROKE_OPACITY
      : DATA_TABLE_CIRCLE_STROKE_OPACITY;
    const op = Array.isArray(activation.query.op)
      ? activation.query.op[0]
      : activation.query.op;
    const label = `${op || "value"}(${activation.query.column})`;
    const valueExpression = buildDataTableValueExpression(
      (legendOnly
        ? ["get", DATA_TABLE_VALUE_PROPERTY]
        : ["feature-state", DATA_TABLE_VALUE_PROPERTY]) as Expression
    );
    const isNoData = [
      "==",
      valueExpression,
      DATA_TABLE_NO_DATA_VALUE,
    ] as Expression;
    const isZero = ["==", valueExpression, 0] as Expression;
    const isPositive = [">", valueExpression, 0] as Expression;
    // UNSET (-2 via coalesce) matches none of these, so missing feature-state
    // stays hidden without Mapbox evaluating `>` / interpolate on null.
    const hasSymbolExpression = [
      "any",
      isNoData,
      isZero,
      isPositive,
    ] as Expression;
    const scaleMin =
      values && values.scaleMax > values.scaleMin ? values.scaleMin : 0;
    const scaleMax =
      values && values.scaleMax > values.scaleMin
        ? values.scaleMax
        : // Single positive value (or empty): keep interpolate valid and
          // clamp that value to the max radius stop.
          Math.max(scaleMin + 1, values?.scaleMax ?? 1);
    const symbolRadius = buildDataTableCircleRadiusExpression({
      valueExpression,
      scaleMin,
      scaleMax,
      zoomDependent: !legendOnly,
      hideWhenMissing: !legendOnly,
      isZero,
    });
    const activeFillColor = loading
      ? DATA_TABLE_LOADING_COLOR
      : DATA_TABLE_ACTIVE_COLOR;
    const activeStrokeColor = loading
      ? "rgba(255, 255, 255, 0.25)"
      : "rgba(255, 255, 255, 0.5)";
    const symbolFillOpacity = loading
      ? fillOpacity
      : ([
          "case",
          isNoData,
          DATA_TABLE_NO_DATA_FILL_OPACITY,
          isZero,
          DATA_TABLE_ZERO_FILL_OPACITY,
          fillOpacity,
        ] as Expression);
    const symbolStrokeOpacity = loading
      ? strokeOpacity
      : ([
          "case",
          isNoData,
          DATA_TABLE_NO_DATA_STROKE_OPACITY,
          isZero,
          DATA_TABLE_CIRCLE_STROKE_OPACITY,
          strokeOpacity,
        ] as Expression);
    const symbolColor = loading
      ? DATA_TABLE_LOADING_COLOR
      : ([
          "case",
          isNoData,
          DATA_TABLE_NO_DATA_COLOR,
          activeFillColor,
        ] as Expression);
    const symbolStrokeColor = loading
      ? activeStrokeColor
      : ([
          "case",
          isNoData,
          DATA_TABLE_NO_DATA_STROKE_COLOR,
          activeStrokeColor,
        ] as Expression);
    const symbolStrokeWidth = loading
      ? 1
      : (["case", isNoData, DATA_TABLE_NO_DATA_STROKE_WIDTH, 1] as Expression);
    return {
      // eslint-disable-next-line i18next/no-literal-string
      id: `${idForLayer(layer, 0)}-data-table`,
      type: "circle",
      source: sourceId,
      ...(sourceLayer ? { "source-layer": sourceLayer } : {}),
      metadata: {
        "s:legend-labels": {
          [DATA_TABLE_VALUE_PROPERTY]: label,
        },
      },
      paint: {
        "circle-color": symbolColor,
        "circle-color-transition": DATA_TABLE_PAINT_TRANSITION,
        "circle-opacity": legendOnly
          ? symbolFillOpacity
          : (["case", hasSymbolExpression, symbolFillOpacity, 0] as Expression),
        "circle-opacity-transition": DATA_TABLE_PAINT_TRANSITION,
        "circle-stroke-color": symbolStrokeColor,
        "circle-stroke-color-transition": DATA_TABLE_PAINT_TRANSITION,
        "circle-stroke-opacity": legendOnly
          ? symbolStrokeOpacity
          : ([
              "case",
              hasSymbolExpression,
              symbolStrokeOpacity,
              0,
            ] as Expression),
        "circle-stroke-opacity-transition": DATA_TABLE_PAINT_TRANSITION,
        "circle-stroke-width": symbolStrokeWidth,
        "circle-radius": symbolRadius,
        // Radius can animate when the expression itself changes (new min/max),
        // but feature-state value updates do not interpolate.
        "circle-radius-transition": DATA_TABLE_PAINT_TRANSITION,
      },
    };
  }

  private setState = (action: SetStateAction<MapContextInterface>) => {
    if (typeof action === "function") {
      this.internalState = {
        ...this.internalState,
        ...action(this.internalState),
      };
    } else {
      this.internalState = { ...this.internalState, ...action };
    }
    this.pushOverlayContexts();
  };

  /**
   * Pushes overlay-related internal state to MapOverlayContext and
   * LegendsContext. Each setter uses a functional update that returns the
   * previous reference when nothing has changed, so React skips re-renders
   * for stable slices.
   */
  private pushOverlayContexts = () => {
    this._setMapOverlayState?.((prev) => {
      if (
        prev.layerStatesByTocStaticId ===
          this.internalState.layerStatesByTocStaticId &&
        prev.styleHash === this.internalState.styleHash
      ) {
        return prev;
      }
      return {
        ...prev,
        layerStatesByTocStaticId: this.internalState.layerStatesByTocStaticId,
        styleHash: this.internalState.styleHash,
      };
    });

    this._setLegendsState?.((prev) => {
      if (prev.legends === this.internalState.legends) {
        return prev;
      }
      return {
        legends: this.internalState.legends,
      };
    });
  };

  /**
   * Pushes the current sketch layer state to the SketchLayerContext.
   * Call this after explicit changes to sketchStates or
   * sketchClassLayerStates that don't go through the stateChanged event
   * (e.g. setVisibleSketches).
   */
  private pushSketchState = () => {
    const sketchState = this.sketchStates.getState();
    const sketchLayerStates: { [id: number]: SketchLayerState } = {};
    for (const key of Object.keys(sketchState)) {
      sketchLayerStates[parseInt(key)] = sketchState[key];
    }
    this._setSketchLayerState?.({
      sketchLayerStates,
      sketchClassLayerStates: {
        ...this.internalState.sketchClassLayerStates,
      },
    });
  };

  setToken(token: string | null | undefined) {
    this.userAccessToken = token;
  }

  setMapAccessToken(token: string | null | undefined) {
    const changed = this.mapAccessToken !== token;
    if (changed) {
      this.dataTableQueryManager.setMapAccessToken(token ?? null);
    }
    this.mapAccessToken = token;
    // Hosted query/column-stats apply bails when the token is missing. Retry
    // once it arrives so a reload race can't leave the legend stuck loading.
    if (changed && token) {
      this.scheduleDataTableFeatureStateSync();
    }
    // Private source URLs include the token, so a new token is a new style.
    if (changed) {
      this.updateStyle();
    }
  }

  setHostedTileUuidsRequiringAuth(uuids: string[] | null | undefined) {
    const next =
      uuids == null
        ? null
        : new global.Set(uuids.map((uuid) => uuid.toLowerCase()));

    const previous = this.hostedTileUuidsRequiringAuth;
    const unchanged =
      previous === next ||
      (previous != null &&
        next != null &&
        previous.size === next.size &&
        Array.from(next).every((uuid) => previous.has(uuid)));

    this.hostedTileUuidsRequiringAuth = next;

    // Private source URLs include the token, so membership changes the style.
    if (!unchanged) {
      this.updateStyle();
    }
  }

  /** Bare URL, plus access_token when this hosted source is private. */
  private styleSourceUrl(url: string): string {
    return hostedStyleUrl(
      url,
      this.mapAccessToken,
      this.hostedTileUuidsRequiringAuth
    );
  }

  /** Whether /v2 requests for this hosted UUID (tiles or GeoJSON) need mapAccessToken. */
  private hostedUuidNeedsMapAccessToken(uuid: string): boolean {
    if (this.hostedTileUuidsRequiringAuth == null) {
      return true;
    }
    return this.hostedTileUuidsRequiringAuth.has(uuid);
  }

  /**
   * Called by reset() and should be called in the client whenever modifying
   * the geoprocessingReferenceId of a TableOfContentsItem so that the manager
   * has up to date information.
   *
   * @param referenceId TableOfContents.geoprocessingReferenceId
   * @param stableId TableOfContents.stableId
   */
  setGeoprocessingReferenceId(referenceId: string, stableId: string) {
    this.geoprocessingReferenceIds[referenceId] = stableId;
  }

  /**
   * Retrieve a list of visible layers. This "reference id" list can be passed
   * to reporting tools as it will first provide the geoprocessingReferenceId of
   * a layer if available, or fall back to the TableOfContents.stableId.
   */
  getVisibleLayerReferenceIds() {
    return this.overlayStates
      .keys()
      .filter((id) => this.overlayStates.getRaw(id)?.visible)
      .map((id) => {
        const reference = this.geoprocessingReferenceIds[id];
        if (reference) {
          return reference;
        } else {
          return id;
        }
      });
  }

  setLoadingOverlay(loadingOverlay: string | null) {
    this.emit("uiUpdate", {
      loadingOverlay: loadingOverlay ?? undefined,
      showLoadingOverlay: loadingOverlay !== null,
    } as Partial<MapUIStateContextState>);
  }

  /**
   * Queries sketch layers to identify ids of sketches visible in the current
   * map viewport. Used by map bookmarks to avoid saving views with private
   * sketches.
   */
  getVisibleSketchIds() {
    if (!this.map) {
      throw new Error("Map not initialized");
    }
    const { layers } = this.computeSketchLayers();
    const features = this.map.queryRenderedFeatures(undefined, {
      layers: layers.map((l) => l.id),
    });
    const ids: { id: number; sharedInForum: boolean }[] = [];
    for (const feature of features) {
      if (feature.properties?.id) {
        const id = parseInt(feature.properties.id);
        if (!ids.find((r) => r.id === id)) {
          ids.push({
            id,
            sharedInForum: Boolean(feature.properties.sharedInForum),
          });
        }
      }
    }
    return ids;
  }

  /**
   * Call whenever the context will be replaced or no longer used
   */
  destroy() {
    this.overlayStates.destroy();
    this.sketchStates.destroy();
    for (const key in this.customSources) {
      this.customSources[key].customSource.destroy();
      delete this.customSources[key];
    }
  }

  /**
   * Create a Mapbox GL JS instance. Should be called by <MapBoxMap /> component.
   * Wait until MapContext.ready = true
   * @param container html div where the map should be rendered
   * @param bounds optionally provide an initial extent
   */
  async createMap(
    container: HTMLDivElement,
    bounds?: [number, number, number, number],
    options?: Partial<MapboxOptions>,
    basemapStateOverride?: BasemapContextState
  ) {
    if (this.mapContainer === container) {
      return;
    }
    this.mapContainer = container;
    if (this.map) {
      this.overlayStates.detachFromMap();
      this.sketchStates.detachFromMap();
      this.map.off("moveend", this.onMapMove);
      this.map.remove();
    }
    for (const key in this.customSources) {
      this.customSources[key].customSource.destroy();
      delete this.customSources[key];
    }
    if (!this._ready) {
      throw new Error(
        "Wait to call createMap until after MapContext.ready = true"
      );
    }
    const effectiveBasemapState = basemapStateOverride ?? this.basemapState;
    if (!effectiveBasemapState?.getSelectedBasemap()?.url) {
      return;
    }
    if (basemapStateOverride) {
      this.basemapState = basemapStateOverride;
    }
    const { style, sprites } = await this.getComputedStyle();
    if (!hasMapboxAccessToken() && styleReferencesMapbox(style)) {
      throw new MissingMapboxAccessTokenError();
    }
    const styleHash = md5(JSON.stringify(style));
    this.setState((prev) => ({ ...prev, styleHash }));

    let mapOptions: MapboxOptions = {
      container,
      style,
      center: this.initialCameraOptions?.center || [1.9, 18.7],
      zoom: this.initialCameraOptions?.zoom || 0.09527381899319892,
      pitch: this.initialCameraOptions?.pitch || 0,
      bearing: this.initialCameraOptions?.bearing || 0,
      maxPitch: 70,
      logoPosition: "bottom-right",
      transformRequest: this.requestTransformer,
    };
    const effectiveBounds = bounds ?? this.initialBounds;
    if (this.initialCameraOptions) {
      mapOptions = {
        ...mapOptions,
        center: this.initialCameraOptions.center,
        zoom: this.initialCameraOptions.zoom,
        pitch: this.initialCameraOptions.pitch,
        bearing: this.initialCameraOptions.bearing,
        ...options,
      };
    } else if (effectiveBounds) {
      mapOptions = {
        ...mapOptions,
        bounds: effectiveBounds,
        ...options,
      };
    } else {
      throw new Error("Both initialBounds and initialCameraOptions are empty");
    }
    this.map = new Map(mapOptions);
    this.dataTableQueryManager.setMap(this.map);

    this.addSprites(sprites, this.map);

    this.overlayStates.attachToMap(this.map);
    this.sketchStates.attachToMap(this.map);
    this.map.on("moveend", this.onMapMove);
    this.map.on("styleimagemissing", this.onStyleImageMissing);
    this.map.on("load", (e) => {
      this.mapIsLoaded = true;
      // The style is installed and the first frame has rendered. Smoke tests
      // wait for this before touching anything else on the map.
      this.map?.getContainer().setAttribute("data-map-loaded", "true");
      this.setState((prev) => ({ ...prev }));
      this._setManagerState?.((prev) => ({ ...prev }));
    });
    return this.map;
  }

  /**
   * Authorize a hosted tiles/uploads URL (access_token, ns, ssn-tr).
   * Shared by transformRequest and raster pixel sampling fetches.
   */
  authorizeHostedUrl = (url: string): string => {
    try {
      const Url = new URL(url);
      if (!isHostedDataHost(Url.hostname)) {
        return url;
      }
      const uuid = extractTilesUuidFromUrl(Url.toString());
      const token =
        uuid &&
        this.hostedUuidNeedsMapAccessToken(uuid) &&
        this.mapAccessToken
          ? this.mapAccessToken
          : null;
      const authorized = withHostedAuthParams(Url.toString(), {
        accessToken: token,
      });
      const outUrl = new URL(authorized);
      // eslint-disable-next-line i18next/no-literal-string
      outUrl.searchParams.set("ssn-tr", "true");
      return outUrl.toString();
    } catch {
      return url;
    }
  };

  requestTransformer = (url: string, resourceType: mapboxgl.ResourceType) => {
    if (/^\/sprites\//.test(url)) {
      const { host, protocol } = window.location;
      if (process.env.NODE_ENV === "development") {
        // Access cloudfront directly. May exhibit CORS issues.
        return {
          // eslint-disable-next-line i18next/no-literal-string
          url: `https://${process.env.REACT_APP_CLOUDFRONT_DOCS_DISTRO}.cloudfront.net${url}?ssn-tr=true`,
        };
      } else {
        // Forward requests to the same origin, which will be routed to the
        // s3 bucket via cloudfront
        return {
          // eslint-disable-next-line i18next/no-literal-string
          url: `${protocol}//${host}${url}?ssn-tr=true`,
        };
      }
    }
    try {
      const Url = new URL(url);
      if (
        this.internalState.offlineTileSimulatorActive &&
        /api\.mapbox\./.test(url)
      ) {
        url = url.replace("api.mapbox.com", "api.mapbox-offline.com");
      } else if (
        this.userAccessToken &&
        Url.host === graphqlURL.host &&
        /\/sketches\/\d+\.geojson.json/.test(url)
      ) {
        const id = url.match(/sketches\/(\d+)\.geojson/)![1];
        const timestamp = this.sketchTimestamps.get(parseInt(id));
        if (timestamp) {
          Url.searchParams.set("timestamp", timestamp);
        }
        return {
          url: Url.toString(),
          // eslint-disable-next-line i18next/no-literal-string
          headers: { authorization: `Bearer ${this.userAccessToken}` },
        };
      } else if (isHostedDataHost(Url.hostname)) {
        return { url: this.authorizeHostedUrl(url) };
      } else if (!/^data:/.test(url)) {
        if (!/gateway.api.globalfishingwatch.org/.test(url)) {
          Url.searchParams.set("ssn-tr", "true");
        }
        url = Url.toString();
      }
      return { url };
    } catch (e) {
      console.error(e);
      return { url };
    }
  };
  /**
   * Adds a local Feature to cache so that the map client need not request
   * GeoJSON or MVT representations of a Sketch. This is useful when editing
   * sketches. The client will already have potentially very large geometries
   * handy from visualizing the preprocessing step. Rather than submitting
   * edits, then waiting for new requests for GeoJSON or vector tiles to update
   * the map, we can make use of this geometry that is already available. This
   * prevents a flash (or long pause) where the map is blank after editing.
   *
   * The cache is not boundless, and items will age out. When they do and the
   * map style is recalculated, they may disappear while requesting new map
   * tiles or GeoJSON content.
   *
   * @param id Sketch ID
   * @param feature Complete GeoJSON feature for representation on the map. Will
   * need to match the same struture and properties of that returned by the
   * GeoJSON & MVT endpoints. To do so, use the Sketch.geojsonProperties GraphQL
   * field to reconstruct from local geometry.
   * @param timestamp Latest timestamp of the sketch. This acts as part of the
   * cache key, so if not matching the latest timestamp in the ToC/GraphQL
   * client cache it will not be used.
   */
  pushLocalSketchGeometryCopy(
    id: number,
    feature: Feature<any>,
    timestamp: string
  ) {
    LocalSketchGeometryCache.set(id, {
      timestamp,
      feature,
    });
  }

  updateLocalSketchGeometryProperties(
    id: number,
    timestamp: string,
    properties: any
  ) {
    const cached = LocalSketchGeometryCache.get(id);
    if (cached) {
      LocalSketchGeometryCache.set(id, {
        ...cached,
        timestamp,
        feature: {
          ...cached.feature,
          properties,
        },
      });
      this.debouncedUpdateStyle();
    } else {
      this.debouncedUpdateStyle();
    }
  }

  onMapMoveDebouncerReference: any | undefined;

  onMapMove = (event: MouseEvent) => {
    if (this.onMapMoveDebouncerReference) {
      clearTimeout(this.onMapMoveDebouncerReference);
    }
    this.onMapMoveDebouncerReference = setTimeout(() => {
      delete this.onMapMoveDebouncerReference;
      this.debouncedUpdatePreferences();
    }, 1000);
  };

  setViewport(center: [number, number], zoom: number) {}

  /**
   * Map will not be rended (state.ready=false) until set.
   * @param feature Rectangulare box will be created from input polygon
   */
  setProjectBounds(feature: Feature<Polygon>) {
    const box = bbox(feature);
    this.initialBounds = box.slice(0, 4) as [number, number, number, number];
    this._ready =
      !!(this.initialCameraOptions || this.initialBounds) &&
      this.basemapsWereSet;
    this._setManagerState?.((prev) => ({ ...prev, ready: this._ready }));
  }

  // computeBasemapOptionalLayerStates has been moved to BasemapProvider

  private applyOptionalBasemapLayerStates(
    baseStyle: Style,
    basemap: BasemapDetailsFragment | undefined
  ) {
    if (!this.basemapState) return;
    const optionalBasemapLayerStates =
      this.basemapState.basemapOptionalLayerStates;

    // If set to true, display the optional layer, else filter out
    const optionalLayersToggleState: { [layerId: string]: boolean } = {};
    for (const layer of basemap?.optionalBasemapLayers || []) {
      if (layer.groupType === OptionalBasemapLayersGroupType.None) {
        for (const id of layer.layers) {
          if (id) {
            optionalLayersToggleState[id] =
              optionalLayersToggleState[id] ||
              !!optionalBasemapLayerStates[layer.id];
          }
        }
      } else {
        // Select or Radio-type basemap
        for (const option of (layer.options || []) as {
          name: string;
          description?: string;
          layers?: string[];
        }[]) {
          // if (optionalBasemapLayerStates[layer.id] !== option.name) {
          // hide all layers associated with this option
          for (const id of option.layers || []) {
            if (id) {
              optionalLayersToggleState[id] =
                optionalLayersToggleState[id] ||
                optionalBasemapLayerStates[layer.id] === option.name;
            }
          }
          // }
        }
      }
    }

    baseStyle.layers = baseStyle.layers.map((layer) => {
      const state = optionalLayersToggleState[layer.id];
      // @ts-ignore
      const hasSource = !!layer.source;
      if (hasSource && state === false) {
        return {
          ...layer,
          ...{
            layout: {
              // @ts-ignore
              ...(layer.layout || {}),
              visibility: "none",
            },
          },
        };
      } else {
        return layer;
      }
    });
  }

  /**
   * Persist the current map session so a reload can restore it.
   *
   * Writes overlay visibility (including data-table intent), camera, and
   * sketch-class display overrides to localStorage under `preferencesKey`.
   * Ephemeral fields like loading/error are omitted. No-op when no key is set.
   */
  private updatePreferences() {
    const sketchClassLayerStates = cloneDeep(
      this.internalState.sketchClassLayerStates || {}
    );
    // strip sketchClassLayerStates of anything other than opacity and zOrderOverride
    for (const id in sketchClassLayerStates) {
      const state = sketchClassLayerStates[id];
      if (state.visible === false) {
        // If the sketch class is not visible, remove it from the states
        delete sketchClassLayerStates[id];
        continue;
      }
      sketchClassLayerStates[id] = {
        opacity: state.opacity,
        zOrderOverride: state.zOrderOverride,
      } as any;
      if (
        !sketchClassLayerStates[id].opacity ||
        sketchClassLayerStates[id].opacity === 1
      ) {
        delete sketchClassLayerStates[id].opacity;
      }
      if (sketchClassLayerStates[id].zOrderOverride === undefined) {
        delete sketchClassLayerStates[id].zOrderOverride;
      }
    }
    if (this.preferencesKey) {
      const prefs = {
        layers: layerStatesForPreferences(this.overlayStates.getState()),
        dataTableSettings: this.dataTableSettingsMemory,
        ...(this.map
          ? {
              cameraOptions: {
                center: this.map.getCenter().toArray(),
                zoom: this.map.getZoom(),
                bearing: this.map.getBearing(),
                pitch: this.map.getPitch(),
              },
            }
          : {}),
        sketchClassLayerStates: sketchClassLayerStates,
      };
      window.localStorage.setItem(this.preferencesKey, JSON.stringify(prefs));
    }
  }

  /** Coalesce rapid map-state changes into a single preferences write. */
  private debouncedUpdatePreferences = debounce(() => {
    this.updatePreferences();
  }, 200);

  resetLayers() {
    const visibleLayerIds = this.overlayStates.keys();
    this.hideTocItems(visibleLayerIds);
    this.overlayStates.retainOnly([]);
    this.debouncedUpdatePreferences();
  }

  private updateStyleDebouncerReference: any | undefined;

  private async debouncedUpdateStyle(backoff = 2) {
    if (this.updateStyleDebouncerReference) {
      clearTimeout(this.updateStyleDebouncerReference);
    }
    this.updateStyleDebouncerReference = setTimeout(() => {
      delete this.updateStyleDebouncerReference;
      this.updateStyle();
    }, backoff);
  }

  private updateStyleInfiniteLoopDetector = 0;
  // iNaturalist sub-sources are tracked via overlayStates.setSourceForKey().

  setDataSourceQueryParameters(id: number | string, params: any) {
    const key = id.toString();
    if (this.clientDataSources[key]) {
      this.clientDataSources[key] = {
        ...this.clientDataSources[key],
        queryParameters: params,
      } as any;
      this.emit("uiUpdate", {
        inaturalistCallToActions: this.computeInatCtas(),
      } as Partial<MapUIStateContextState>);
      this.debouncedUpdateStyle();
    }
  }

  private computeInatCtas() {
    const ctas: { projectId: string; label?: string }[] = [];
    const seen = new Set<string>();
    for (const stableId of this.overlayStates.keys()) {
      const state = this.overlayStates.getRaw(stableId);
      if (!state?.visible) continue;
      const layer = this.layers[stableId];
      if (!layer) continue;
      const source =
        this.archivedSource && this.archivedSource.dataLayerId === layer.id
          ? this.archivedSource.dataSource
          : this.clientDataSources[layer.dataSourceId];
      if (source?.type === DataSourceTypes.Inaturalist) {
        const params = normalizeInaturalistParams(
          (source.queryParameters as any) || {}
        );
        if (params.showCallToAction && params.projectId) {
          const key = params.projectId;
          if (!seen.has(key)) {
            seen.add(key);
            ctas.push({
              projectId: params.projectId,
              label: this.tocItems[stableId]?.label,
            });
          }
        }
      }
    }
    return ctas;
  }

  async updateStyle() {
    if (!this.basemapState?.getSelectedBasemap()?.url) {
      this.debouncedUpdateStyle();
      return;
    }
    if (this.map && this._ready) {
      this.updateStyleInfiniteLoopDetector++;
      this.updateStyleInfiniteLoopDetector = 0;
      if (this.updateStyleInfiniteLoopDetector > 10) {
        this.updateStyleInfiniteLoopDetector = 0;
        throw new Error("Infinite loop");
      }
      const { style, sprites, sketchLayerIds } = await this.getComputedStyle(
        () => {
          this.debouncedUpdateStyle();
        }
      );
      this.emit("sketchLayerIdsChanged", sketchLayerIds);
      const styleHash = md5(JSON.stringify(style));
      if (styleHash === this.internalState.styleHash) {
        // Style is unchanged, but catalog / token / sources may have become
        // ready since the last settle — still retry feature-state apply.
        this.scheduleDataTableFeatureStateSync();
        return;
      }
      this.addSprites(sprites, this.map);
      const update = () => {
        if (!this.map) {
          return;
        }
        this.map.setStyle(style);
        for (const id in this.customSources) {
          const { visible, listenersAdded, customSource } =
            this.customSources[id];
          // Make sure event listeners are added
          if (visible && !listenersAdded) {
            customSource.addEventListeners(this.map);
            this.customSources[id].listenersAdded = true;
            this.customSources[id].lastUsedTimestamp = new Date().getTime();
          } else if (!visible && listenersAdded) {
            customSource.removeEventListeners(this.map);
            this.customSources[id].listenersAdded = false;
          }
        }
        this.pruneInactiveCustomSources();
        for (const id in this.customSources) {
          const sourceConfig = this.clientDataSources[id];
          const { customSource, visible } = this.customSources[id];
          if (visible && sourceConfig) {
            if (isArcGISDynamicMapService(customSource)) {
              customSource.updateUseDevicePixelRatio(
                sourceConfig.useDevicePixelRatio || false
              );
              customSource.updateQueryParameters(
                sourceConfig.queryParameters || {}
              );
            } else if (isArcgisFeatureLayerSource(customSource)) {
              customSource.updateFetchStrategy(
                sourceConfig.arcgisFetchStrategy ===
                  ArcgisFeatureLayerFetchStrategy.Raw
                  ? "raw"
                  : sourceConfig.arcgisFetchStrategy ===
                    ArcgisFeatureLayerFetchStrategy.Tiled
                  ? "tiled"
                  : "auto"
              );
            }
          }
        }
        const sources: { [id: string]: CustomGLSource<any> } = {};
        for (const id in this.customSources) {
          const { customSource, visible } = this.customSources[id];
          if (visible) {
            sources[id] = customSource;
          }
        }
        this.emit("customSourcesChanged", sources);
        this.setState((prev) => ({ ...prev, styleHash }));
        // Style diffs may preserve feature-state on reused sources. Sync
        // reconciles markers and only re-fetches when state was actually lost.
        this.scheduleDataTableFeatureStateSync();
      };
      if (!this.mapIsLoaded) {
        setTimeout(update, 20);
      } else {
        update();
      }
    } else {
      this.updateStyleInfiniteLoopDetector++;
      if (this.updateStyleInfiniteLoopDetector > 10) {
        this.updateStyleInfiniteLoopDetector = 0;
      } else {
        this.debouncedUpdateStyle();
      }
    }
  }

  /**
   * Emits the current sketch layer IDs for late-joining listeners (e.g. when
   * MapUIProvider's interactivityManager is created after the style was loaded).
   */
  emitCurrentSketchLayerIds() {
    if (
      this.map &&
      this._ready &&
      this.basemapState?.getSelectedBasemap()?.url
    ) {
      this.getComputedStyle().then(({ sketchLayerIds }) => {
        this.emit("sketchLayerIdsChanged", sketchLayerIds);
      });
    }
  }

  private pruneInactiveCustomSources() {
    // prune customSources, removing non-active sources that haven't been used
    // in a while
    let inactiveSources: {
      id: string;
      customSource: CustomGLSource<any>;
      timestamp: number;
    }[] = [];
    // collect inactive sources
    for (const id in this.customSources) {
      const { visible, lastUsedTimestamp, customSource } =
        this.customSources[id];
      if (!visible) {
        inactiveSources.push({
          id,
          customSource,
          timestamp: lastUsedTimestamp,
        });
      }
    }

    if (inactiveSources.length > STALE_CUSTOM_SOURCE_SIZE) {
      // sort inactiveSources by lastUsedTimestamp, in descending order
      inactiveSources.sort((a, b) => b.timestamp - a.timestamp);
      inactiveSources = inactiveSources.slice(0, STALE_CUSTOM_SOURCE_SIZE);
      for (const id in this.customSources) {
        if (inactiveSources.find((s) => s.id === id)) {
          this.customSources[id].customSource.destroy();
          delete this.customSources[id];
        }
      }
    }
  }

  /**
   * Set the visible overlays associated with TableOfContentsItems
   * @param ids List of ids. Can be the TableOfContents.stableId or geoprocessingReferenceId
   */
  async setVisibleTocItems(ids: string[]) {
    const stableIds = ids.map((id) =>
      id in this.geoprocessingReferenceIds
        ? this.geoprocessingReferenceIds[id]
        : id
    );

    for (const id of this.overlayStates.keys()) {
      if (stableIds.indexOf(id) === -1) {
        this.hideTocItem(id);
      }
    }
    for (const id of stableIds) {
      if (!this.overlayStates.has(id)) {
        this.showTocItem(id);
      }
    }
    this.debouncedUpdateStyle();
    this.updateLegends();
  }

  /**
   * Hide the overlay associated with a TableOfContentsItem
   * @param id Can be the TableOfContents.stableId or geoprocessingReferenceId
   */
  private hideTocItem(id: string) {
    const stableId =
      id in this.geoprocessingReferenceIds
        ? this.geoprocessingReferenceIds[id]
        : id;
    const state = this.overlayStates.getRaw(stableId);
    if (state?.visible) {
      if (
        (state.opacity !== undefined && state.opacity !== 1) ||
        state.zOrderOverride !== undefined
      ) {
        this.overlayStates.setVisible(stableId, false);
      } else {
        this.overlayStates.removeLayer(stableId);
      }
    }
    this.updateLegends();
  }

  /**
   * Show the overlay associated with a TableOfContentsItem
   * @param id Can be the TableOfContents.stableId or geoprocessingReferenceId
   */
  private showTocItem(id: string) {
    const stableId =
      id in this.geoprocessingReferenceIds
        ? this.geoprocessingReferenceIds[id]
        : id;
    if (this.overlayStates.has(stableId)) {
      const state = this.overlayStates.getRaw(stableId)!;
      if (state.error) {
        // do nothing
      } else {
        if (!state.visible) {
          this.overlayStates.setVisible(stableId, true);
          this.overlayStates.setLoading(stableId, true);
          this.overlayStates.setHidden(stableId, false);
        }
      }
    } else {
      this.overlayStates.addLayer(stableId, {
        loading: true,
        visible: true,
        opacity:
          this.internalState.layerStatesByTocStaticId[stableId]?.opacity || 1,
        hidden: false,
        zOrderOverride:
          this.internalState.layerStatesByTocStaticId[stableId]?.zOrderOverride,
      });
      const layer = this.layers[stableId];
      if (layer) {
        this.overlayStates.setSourceForKey(
          stableId,
          this.overlayStates.prefixedSourceId(layer.dataSourceId)
        );
      }
    }
    this.updateLegends();
  }

  /**
   *
   * @param sketches
   */
  setVisibleSketches(
    sketches: {
      id: number;
      timestamp?: string;
      sketchClassId?: number;
      filterMvtUrl?: string;
    }[]
  ) {
    const sketchIdStrings = new Set(sketches.map(({ id }) => id.toString()));
    // remove missing ids
    this.sketchStates.retainOnly(sketchIdStrings);
    // add new sketches
    for (const sketch of sketches) {
      const key = sketch.id.toString();
      const existing = this.sketchStates.getRaw(key);
      if (!existing?.visible) {
        this.sketchStates.addLayer(key, {
          loading: false,
          visible: true,
          sketchClassId: sketch.sketchClassId,
          filterMvtUrl: sketch.filterMvtUrl,
        });
        // Register source mapping for event triage
        // eslint-disable-next-line i18next/no-literal-string
        this.sketchStates.setSourceForKey(key, `sketch-${sketch.id}`);
      } else {
        this.sketchStates.patch(key, {
          filterMvtUrl: sketch.filterMvtUrl,
        } as Partial<SketchLayerState>);
      }
    }
    // update public state
    this.pushSketchState();
    for (const sketch of sketches) {
      if (sketch.timestamp) {
        this.sketchTimestamps.set(sketch.id, sketch.timestamp);
      } else {
        this.sketchTimestamps.delete(sketch.id);
      }
    }
    // update sketchClassLayerStates
    for (const id in this.internalState.sketchClassLayerStates) {
      this.internalState.sketchClassLayerStates[id].visible = false;
      // see if any sketches are visible for this sketch class
      for (const skKey of this.sketchStates.keys()) {
        const skState = this.sketchStates.getRaw(skKey);
        if (skState?.sketchClassId === parseInt(id) && skState.visible) {
          this.internalState.sketchClassLayerStates[id].visible = true;
          break;
        }
      }
    }
    // request a redraw
    this.debouncedUpdateStyle();
    this.updateLegends();
  }

  getSketchClassGLStyles(sketchClassId: number): AnyLayer[] {
    // Clone the style layers. Mapbox GL JS will freeze the objects after adding
    // to the map, which will prevent us from modifying the style later and
    // throw exceptions
    return cloneDeep(
      this.internalState.sketchClassLayerStates[sketchClassId]?.styles ||
        this.defaultSketchLayers
    );
  }

  setSketchClassLayerConfig(config: {
    [sketchClassId: number]: {
      styles: any[];
      legendItem: LegendItem;
      name: string;
    };
  }) {
    for (const key in config) {
      const existingState = this.internalState.sketchClassLayerStates[key];
      this.internalState.sketchClassLayerStates[key] = {
        ...{
          visible: false,
          hidden: false,
          loading: false,
          error: undefined,
        },
        ...(existingState || {}),
        ...config[key],
        sketchClassId: parseInt(key),
      };
    }
    this.pushSketchState();
    this.debouncedUpdatePreferences();
    this.debouncedUpdateStyle();
    this.updateLegends();
  }

  setSelectedSketches(sketchIds: number[]) {
    this.selectedSketches = sketchIds;
    // request a redraw
    this.debouncedUpdateStyle();
  }

  markSketchAsEditable(sketchId: number) {
    this.editableSketchId = sketchId;
    this.emit("focusedSketchIdChanged", sketchId);
    // request a redraw
    this.debouncedUpdateStyle();
  }

  unmarkSketchAsEditable() {
    delete this.editableSketchId;
    this.emit("focusedSketchIdChanged", null);
    // request a redraw
    this.debouncedUpdateStyle();
  }

  hideEditableSketch(sketchId: number) {
    this.hideEditableSketchId = sketchId;
    this.sketchStates.excludeFromMonitoring(sketchId.toString());
    this.sketchStates.setLoading(sketchId.toString(), false);
    this.debouncedUpdateStyle();
  }

  unhideEditableSketch() {
    if (this.hideEditableSketchId !== undefined) {
      this.sketchStates.includeInMonitoring(
        this.hideEditableSketchId.toString()
      );
    }
    delete this.hideEditableSketchId;
    this.debouncedUpdateStyle();
  }

  clearSketchEditingState() {
    if (this.hideEditableSketchId !== undefined) {
      this.sketchStates.includeInMonitoring(
        this.hideEditableSketchId.toString()
      );
    }
    delete this.hideEditableSketchId;
    this.unmarkSketchAsEditable();
    this.debouncedUpdateStyle();
  }

  async getComputedBaseStyle() {
    if (!this.basemapState) {
      throw new Error(
        "Basemap state not set; ensure MapManagerContextProvider is nested under BasemapProvider"
      );
    }
    const basemap = this.basemapState.getSelectedBasemap();
    const labelsID = basemap?.labelsLayerId;
    // if (!basemap?.url) {
    //   throw new Error("No basemap URL set");
    // }
    const url =
      basemap?.url ||
      "mapbox://styles/underbluewaters/cklb3vusx2dvs17pay6jp5q7e";
    let baseStyle: Style;
    if (basemap?.type === BasemapType.RasterUrlTemplate) {
      let url = basemap.url;
      if (
        url.indexOf("services.arcgisonline.com") > -1 &&
        process.env.REACT_APP_ARCGIS_DEVELOPER_API_KEY
      ) {
        // eslint-disable-next-line i18next/no-literal-string
        url += `?token=${process.env.REACT_APP_ARCGIS_DEVELOPER_API_KEY}`;
      }
      baseStyle = {
        version: 8,
        // TODO: choose a ip un-encumbered alternative for these
        glyphs: "mapbox://fonts/mapbox/{fontstack}/{range}.pbf",
        sprite: "mapbox://sprites/mapbox/streets-v11",
        sources: {
          raster: {
            type: "raster",
            tiles: [url],
            tileSize: 256,
            ...(basemap.maxzoom ? { maxzoom: basemap.maxzoom } : {}),
          },
        },
        layers: [
          {
            id: "bg",
            type: "background",
            paint: {
              "background-color": "#efefef",
            },
          },
          {
            id: "raster-tiles",
            type: "raster",
            source: "raster",
            minzoom: 0,
            maxzoom: 22,
          },
        ],
      };
    } else {
      try {
        baseStyle = await fetchGlStyle(url);
        // Clear any previous style error
        this._setManagerState?.((prev) =>
          prev.styleError ? { ...prev, styleError: undefined } : prev
        );
      } catch (e) {
        if (!shouldAttemptMapboxStyleFallback(e)) {
          throw e;
        }
        // Set styleError on MapManagerState
        this._setManagerState?.((prev) => ({
          ...prev,
          styleError: e instanceof Error ? e : new Error(String(e)),
        }));
        console.warn(e);
        baseStyle = await fetchGlStyle(
          "mapbox://styles/underbluewaters/cklb5eho20sb817qhmzltsrpf"
        );
      }
    }
    baseStyle = {
      ...baseStyle,
      layers: [...(baseStyle.layers || [])],
      sources: { ...(baseStyle.sources || {}) },
      // @ts-ignore
      terrain: undefined,
    };
    if (this.basemapState?.terrainEnabled && basemap) {
      const newSource = {
        type: "raster-dem",
        url: basemap.terrainUrl,
      };
      /**
       * This extra check for an existing source is required because the terrain tilejson
       * may be loaded already by mapbox gl. Calling setStyle with the un-initialized source
       * will trigger a repaint of the entire style. This seems to be a bug with mapbox-gl
       * because other source types do not have this problem
       */
      let existingSource: AnySourceImpl | null = null;
      try {
        existingSource = this.map?.getSource("terrain-source") || null;
      } catch (e) {
        // Do nothing. Sometimes the map will be set but calling getSource will
        // throw an error from deep in the mapbox-gl code.
        // Attempts to fix bug reported by sentry:
        // https://sentry.io/organizations/wwwseasketchorg/issues/2988858003/events/4f007357f24245989c1bc267d04f485c
      }
      if (
        existingSource &&
        existingSource.type === "raster-dem" &&
        existingSource.url === newSource.url &&
        existingSource.encoding &&
        existingSource.tiles
      ) {
        baseStyle.sources!["terrain-source"] = {
          type: existingSource.type,
          url: existingSource.url,
          bounds: existingSource.bounds,
          tiles: existingSource.tiles,
          encoding: existingSource.encoding,
          tileSize: existingSource.tileSize,
        };
      } else {
        // @ts-ignore
        baseStyle.sources![baseStyle.terrain?.source || "terrain-source"] =
          newSource;
      }

      // @ts-ignore
      baseStyle.terrain = {
        source: "terrain-source",
        exaggeration: parseFloat(basemap.terrainExaggeration || 1.2),
      };

      if (!(baseStyle.layers || []).find((l) => l.type === "sky")) {
        baseStyle.layers?.push({
          id: "sky",
          type: "sky",
          paint: {
            // set up the sky layer to use a color gradient
            "sky-type": "gradient",
            // the sky will be lightest in the center and get darker moving radially outward
            // this simulates the look of the sun just below the horizon
            "sky-gradient": [
              "interpolate",
              ["linear"],
              ["sky-radial-progress"],
              0.8,
              "rgba(135, 206, 235, 1.0)",
              1,
              "rgba(0,0,0,0.1)",
            ],
            "sky-gradient-center": [0, 0],
            "sky-gradient-radius": 90,
            "sky-opacity": [
              "interpolate",
              ["exponential", 0.1],
              ["zoom"],
              5,
              0,
              22,
              1,
            ],
          },
        });
      }
    }

    let labelsLayerIndex = baseStyle.layers?.findIndex(
      (layer) => layer.id === labelsID
    );
    baseStyle.sources = baseStyle.sources || {};

    baseStyle.layers = baseStyle.layers || [];
    if (labelsLayerIndex === -1) {
      labelsLayerIndex = baseStyle.layers.length;
    }
    return { baseStyle, labelsLayerIndex, basemap };
  }

  async getComputedStyle(unfinishedCustomSourceCallback?: () => void): Promise<{
    style: Style;
    sprites: SpriteDetailsFragment[];
    sketchLayerIds: string[];
  }> {
    // this.resetLayersByZIndex();
    // get mapbox-gl-draw related layers and sources and make sure to include
    // them in the end. gl-draw has some magic to avoid their layers getting
    // removed but I'm seeing errors like this when the style is updated:
    //
    // Uncaught TypeError: Cannot read properties of undefined (reading 'get')
    //    at new Ut (mapbox-gl.js:36:1)
    let glDrawLayers: AnyLayer[] = [];
    let glDrawSources: { [id: string]: GeoJSONSource } = {};
    let existingStyle: mapboxgl.Style | undefined;
    try {
      existingStyle = this.map?.getStyle();
    } catch (e) {
      // do nothing
    }
    if (existingStyle) {
      glDrawLayers =
        existingStyle.layers?.filter((l) => l.id.indexOf("gl-draw") === 0) ||
        [];
      // @ts-ignore
      const relatedSourceIds = glDrawLayers.map((l) => l.source || "");
      for (const key in existingStyle.sources) {
        if (relatedSourceIds.indexOf(key) > -1) {
          glDrawSources[key] = existingStyle.sources[key] as GeoJSONSource;
        }
      }
    }

    // Do the same for filter-layer- layers related to FilterLayerManager
    let filterLayers: AnyLayer[] = [];
    let filterSources: { [id: string]: VectorSource } = {};
    if (existingStyle) {
      filterLayers =
        existingStyle.layers?.filter(
          (l) => l.id.indexOf("filter-layer-") === 0
        ) || [];
      // @ts-ignore
      const relatedSourceIds = filterLayers.map((l) => l.source || "");
      for (const key in existingStyle.sources) {
        if (relatedSourceIds.indexOf(key) > -1) {
          filterSources[key] = existingStyle.sources[key] as VectorSource;
        }
      }
      // look for all-cells layer
      const allCellsLayer = existingStyle.layers?.find(
        (l) => l.id === "all-cells"
      );
      if (allCellsLayer) {
        filterLayers.push(allCellsLayer);
      }
      // look for all-cells source
      const allCellsSource = existingStyle.sources["all-cells"];
      if (allCellsSource) {
        filterSources["all-cells"] = allCellsSource as VectorSource;
      }
    }

    const { baseStyle, labelsLayerIndex, basemap } =
      await this.getComputedBaseStyle();

    let sprites: SpriteDetailsFragment[] = [];
    const inaturalistGeneratedLayers: {
      [layerId: string]: AnyLayer[];
    } = {};
    let underLabels: any[] = baseStyle.layers.slice(0, labelsLayerIndex);
    let overLabels: any[] = baseStyle.layers.slice(labelsLayerIndex);
    let isUnderLabels = true;
    const layersByZIndex = this.getVisibleLayersByZIndex();
    let i = layersByZIndex.length;
    const insertedCustomSourceIds: number[] = [];
    const sketchClassLayers = this.computeSketchLayers();
    const sketchLayerIds: string[] = [];

    // reset sublayer settings before proceeding
    while (i--) {
      if (layersByZIndex[i].sketchClassLayerState?.sketchClassId) {
        const state = layersByZIndex[i].sketchClassLayerState!;
        // add all sources and layers for this sketchClassLayer
        const { layers: layerIds, sources: sourceIds } =
          sketchClassLayers.sketchClassLayers[state.sketchClassId];
        // add sources to the end of the list
        for (const sourceId of sourceIds) {
          if (!(sourceId in baseStyle.sources)) {
            const source = sketchClassLayers.sources[sourceId];
            if (source) {
              baseStyle.sources[sourceId] = source;
            }
          }
        }
        // add layers
        const layers = sketchClassLayers.layers.filter(
          (l) => layerIds.indexOf(l.id) > -1
        );
        if (layers.length && state.hidden !== true) {
          // add layers to the end of the list
          underLabels.push(...layers);
          // add sketch class layer id to the list of sketch layer ids
          sketchLayerIds.push(...layers.map((l) => l.id));
        }
        continue;
      }
      if (!layersByZIndex[i].dataLayer) {
        throw new Error(`Layer ${layersByZIndex[i].id} has no dataLayer`);
      }
      const layerId = layersByZIndex[i].dataLayer!.tocId;
      if (layerId === "LABELS") {
        isUnderLabels = false;
      } else {
        if (this.overlayStates.getRaw(layerId)?.visible) {
          const layer = this.layers[layerId];
          // If layer or source are not set yet, they will be ignored
          if (layer) {
            const source =
              this.archivedSource &&
              this.archivedSource.dataLayerId === layer.id
                ? this.archivedSource.dataSource
                : this.clientDataSources[layer.dataSourceId];
            if (source) {
              // Add the source
              const overlaySourceKey = this.overlayStates.prefixedSourceId(
                source.id
              );

              let dataTableVisualizationSettings =
                this.resolveDataTableVisualizationSettings(layerId);

              if (!dataTableVisualizationSettings?.table.joinColumn) {
                dataTableVisualizationSettings = undefined;
              }

              const promoteId =
                dataTableVisualizationSettings?.table.overlayJoinColumn;

              if (!baseStyle.sources[overlaySourceKey]) {
                switch (source.type) {
                  case DataSourceTypes.Vector:
                    baseStyle.sources[overlaySourceKey] = {
                      type: "vector",
                      attribution: source.attribution || "",
                      tiles: (source.tiles as string[]).map((tile) =>
                        this.styleSourceUrl(tile)
                      ),
                      ...(promoteId ? { promoteId } : {}),
                      ...(source.bounds
                        ? { bounds: source.bounds.map((b) => parseFloat(b)) }
                        : {}),
                      ...(source.minzoom !== undefined
                        ? {
                            minzoom: parseInt(
                              source.minzoom?.toString() || "0"
                            ),
                          }
                        : {}),
                      ...(source.maxzoom !== undefined
                        ? {
                            maxzoom: parseInt(
                              source.maxzoom?.toString() || "0"
                            ),
                          }
                        : {}),
                    };
                    break;
                  case DataSourceTypes.SeasketchMvt:
                    let url = source.url!;
                    if (!/\.json$/.test(url)) {
                      url += ".json";
                    }
                    baseStyle.sources[overlaySourceKey] = {
                      type: "vector",
                      url: this.styleSourceUrl(url),
                      attribution: source.attribution || "",
                      ...(promoteId ? { promoteId } : {}),
                    };
                    break;
                  case DataSourceTypes.SeasketchVector:
                  case DataSourceTypes.Geojson:
                    baseStyle.sources[overlaySourceKey] = {
                      type: "geojson",
                      data: this.styleSourceUrl(source.url!),
                      attribution: source.attribution || "",
                      ...(promoteId ? { promoteId } : {}),
                    };
                    break;
                  case DataSourceTypes.SeasketchRaster:
                    if (source.url) {
                      let url = source.url!;
                      if (!/\.json$/.test(url)) {
                        url += ".json";
                      }
                      baseStyle.sources[overlaySourceKey] = {
                        type: "raster",
                        url: this.styleSourceUrl(url),
                        attribution: source.attribution || "",
                      };
                    } else {
                      throw new Error("Not implemented");
                    }
                    break;
                  case DataSourceTypes.Inaturalist: {
                    const { sources: inatSources, layers: inatLayers } =
                      buildInaturalistSourcesAndLayers(
                        (source.queryParameters as any) || {},
                        {
                          sourceIdBase: overlaySourceKey,
                          layerIdBase: layer.tocId || overlaySourceKey,
                          attribution: source.attribution || "",
                          opacity:
                            this.overlayStates.getRaw(layerId)?.opacity !==
                            undefined
                              ? this.overlayStates.getRaw(layerId)!.opacity
                              : 1,
                        }
                      );
                    baseStyle.sources = {
                      ...baseStyle.sources,
                      ...inatSources,
                    };
                    const inatSourceIds = Object.keys(inatSources);
                    for (const inatSrcId of inatSourceIds) {
                      this.overlayStates.setSourceForKey(layerId, inatSrcId);
                    }
                    inaturalistGeneratedLayers[layerId] = inatLayers;
                    break;
                  }
                  case DataSourceTypes.ArcgisVector:
                  case DataSourceTypes.ArcgisRasterTiles:
                  case DataSourceTypes.ArcgisDynamicMapserver:
                    // Sublayers can be represented multiple times, so don't
                    // add the source if it's already there
                    if (!insertedCustomSourceIds.includes(source.id)) {
                      insertedCustomSourceIds.push(source.id);
                      if (!this.customSources[source.id]) {
                        switch (source.type) {
                          case DataSourceTypes.ArcgisVector:
                          case DataSourceTypes.ArcgisRasterTiles:
                          case DataSourceTypes.ArcgisDynamicMapserver:
                            this.customSources[source.id] = {
                              listenersAdded: false,
                              visible: true,
                              lastUsedTimestamp: new Date().getTime(),
                              customSource: createCustomSource(
                                source,
                                this.arcgisRequestManager,
                                overlaySourceKey
                              ),
                            };
                            break;
                          default:
                            throw new Error(
                              `CustomGLSource not yet supported for ${source.type}`
                            );
                        }
                        // Initialize the source
                        const { customSource } = this.customSources[source.id];
                        customSource.prepare().then(async () => {
                          if (unfinishedCustomSourceCallback) {
                            unfinishedCustomSourceCallback();
                          }
                        });
                      } else {
                        delete this.customSources[source.id].sublayers;
                      }
                      this.customSources[source.id].visible = true;
                      // add style if ready
                      const { customSource } = this.customSources[source.id];
                      // Adding the source is skipped until later when sublayers are setup
                      if (customSource.ready) {
                        const styleData = await customSource.getGLStyleLayers();
                        if (styleData.imageList && this.map) {
                          styleData.imageList.addToMap(this.map);
                        }
                        const layers = isUnderLabels ? underLabels : overLabels;
                        if (
                          !this.overlayStates.getRaw(layerId)?.hidden ||
                          source.type ===
                            DataSourceTypes.ArcgisDynamicMapserver
                        ) {
                          let layersToAdd = styleData.layers;
                          const _overlayState =
                            this.overlayStates.getRaw(layerId);
                          if (
                            source.type !==
                              DataSourceTypes.ArcgisDynamicMapserver &&
                            _overlayState &&
                            "opacity" in _overlayState &&
                            typeof _overlayState.opacity === "number"
                          ) {
                            layersToAdd = adjustLayerOpacities(
                              layersToAdd as mapboxgl.AnyLayer[],
                              _overlayState.opacity!
                            );
                          }
                          if (
                            layer.interactivitySettings &&
                            layer.interactivitySettings.type ===
                              InteractivityType.SidebarOverlay
                          ) {
                            layers.push(
                              ...addInteractivityExpressions(
                                layersToAdd as AnyLayer[]
                              )
                            );
                          } else {
                            layers.push(...layersToAdd);
                          }
                        }
                      } else {
                        setTimeout(() => {
                          this.debouncedUpdateStyle();
                        }, 50);
                      }
                    }
                    break;
                  default:
                    break;
                }
              }
              // Add the sprites if needed
              if (
                this.archivedSource &&
                this.archivedSource.dataLayerId === layer.id &&
                this.archivedSource.sprites?.length
              ) {
                sprites = [...sprites, ...this.archivedSource.sprites];
              } else if (layer.sprites?.length) {
                sprites = [...sprites, ...layer.sprites];
              }

              // Add the layer(s) for static sources (non-CustomGLSource's)
              if (
                (source.type === DataSourceTypes.SeasketchVector ||
                  source.type === DataSourceTypes.Geojson ||
                  source.type === DataSourceTypes.Vector ||
                  source.type === DataSourceTypes.SeasketchRaster ||
                  // source.type === DataSourceTypes.ArcgisVector ||
                  source.type === DataSourceTypes.SeasketchMvt) &&
                layer.mapboxGlStyles?.length
              ) {
                const shouldHaveSourceLayer =
                  source.type === DataSourceTypes.SeasketchMvt ||
                  source.type === DataSourceTypes.Vector; // ||
                // source.type === DataSourceTypes.SeasketchRaster;
                const staticLayers = (
                  this.archivedSource &&
                  this.archivedSource.dataLayerId === layer.id &&
                  this.archivedSource.mapboxGlStyle
                    ? this.archivedSource.mapboxGlStyle
                    : layer.mapboxGlStyles
                ) as any[];
                let glLayers = staticLayers.map((lyr, i) => {
                  return {
                    ...lyr,
                    source: overlaySourceKey,
                    id: idForLayer(layer, i),
                    ...(shouldHaveSourceLayer
                      ? {
                          "source-layer":
                            this.archivedSource &&
                            this.archivedSource.dataLayerId === layer.id
                              ? this.archivedSource.sourceLayer
                              : layer.sourceLayer,
                        }
                      : {}),
                  } as AnyLayer;
                });
                const _staticOverlay = this.overlayStates.getRaw(layerId);
                const sourceLayer = shouldHaveSourceLayer
                  ? this.archivedSource &&
                    this.archivedSource.dataLayerId === layer.id
                    ? this.archivedSource.sourceLayer || undefined
                    : layer.sourceLayer || undefined
                  : undefined;
                /**
                 * # Data Table proportional symbol layer calculation
                 */
                if (
                  dataTableVisualizationSettings &&
                  dataTableVisualizationSettings.query.column &&
                  dataTableVisualizationSettings.query.op
                ) {
                  glLayers = [
                    this.buildDataTableProportionalSymbolLayer(
                      layer,
                      overlaySourceKey,
                      sourceLayer
                    ),
                  ];
                }
                if (
                  _staticOverlay &&
                  "opacity" in _staticOverlay &&
                  typeof _staticOverlay.opacity === "number"
                ) {
                  glLayers = adjustLayerOpacities(
                    glLayers,
                    _staticOverlay.opacity!
                  );
                }
                const layers = isUnderLabels ? underLabels : overLabels;
                if (!this.isLegendEyeHidden(layerId)) {
                  if (
                    layer.interactivitySettings?.type ===
                    InteractivityType.SidebarOverlay
                  ) {
                    glLayers = addInteractivityExpressions(glLayers);
                  }
                  if (this.temporallyHiddenTocIds.has(layerId)) {
                    glLayers = applyClockPaintHidden(
                      glLayers
                    ) as AnyLayer[];
                  } else if (
                    canKeepTilesWhenClockHidden(source.type) &&
                    isTemporalInfo(source.temporal)
                  ) {
                    glLayers = snapClockOpacityTransitions(
                      glLayers
                    ) as AnyLayer[];
                  }
                  layers.push(...glLayers);
                }
              } else if (source.type === DataSourceTypes.Inaturalist) {
                const inatLayers = inaturalistGeneratedLayers[layerId];
                if (inatLayers && !this.isLegendEyeHidden(layerId)) {
                  const targetLayers = isUnderLabels ? underLabels : overLabels;
                  targetLayers.push(
                    ...(this.temporallyHiddenTocIds.has(layerId)
                      ? (applyClockPaintHidden(inatLayers) as AnyLayer[])
                      : isTemporalInfo(source.temporal)
                      ? (snapClockOpacityTransitions(
                          inatLayers
                        ) as AnyLayer[])
                      : inatLayers)
                  );
                }
              } else if (
                isCustomSourceType(source.type) &&
                (layer.sublayer ||
                  (this.archivedSource &&
                    this.archivedSource.dataLayerId === layer.id &&
                    this.archivedSource.sublayer))
              ) {
                // Add sublayer info if needed
                if (!Array.isArray(this.customSources[source.id].sublayers)) {
                  this.customSources[source.id].sublayers = [];
                }
                const settings = this.overlayStates.getRaw(layerId);
                if (!settings) {
                  throw new Error("Visible layer settings missing");
                }
                const sublayer =
                  this.archivedSource &&
                  this.archivedSource.dataLayerId === layer.id &&
                  this.archivedSource.sublayer
                    ? this.archivedSource.sublayer
                    : layer.sublayer;
                if (!this.isLegendEyeHidden(layerId)) {
                  this.customSources[source.id].sublayers!.unshift({
                    id: sublayer!,
                    opacity:
                      "opacity" in settings && settings.opacity !== undefined
                        ? settings.opacity
                        : 1,
                  });
                }
              }
            }
          }
        }
      }
    }

    // mark customSources that are not visible as inactive, and remove their
    // event listeners
    for (const id in this.customSources) {
      if (!insertedCustomSourceIds.includes(parseInt(id))) {
        this.customSources[id].visible = false;
      } else {
        const { customSource, sublayers } = this.customSources[id];
        if (customSource.ready) {
          if (sublayers) {
            customSource.updateLayers(sublayers);
          }
          const glSource = await customSource.getGLSource(this.map!);
          baseStyle.sources[this.overlayStates.prefixedSourceId(id)] = glSource;
        }
      }
    }

    baseStyle.sources = {
      ...baseStyle.sources,
      ...this.dynamicDataSources,
      ...glDrawSources,
      ...filterSources,
    };

    baseStyle.layers = [
      ...underLabels,
      ...overLabels,
      ...this.dynamicLayers,
      ...glDrawLayers,
      ...filterLayers,
    ];

    // Evaluate any basemap optional layers
    this.applyOptionalBasemapLayerStates(baseStyle, basemap);

    // // Add sketches
    // const sketchLayerIds: string[] = [];
    // const sketchData = this.computeSketchLayers();
    // baseStyle.layers.push(...sketchData.layers);
    // sketchLayerIds.push(
    //   ...sketchData.layers.filter((l) => l.type !== "symbol").map((l) => l.id)
    // );
    // for (const sourceId in sketchData.sources) {
    //   baseStyle.sources[sourceId] = sketchData.sources[sourceId];
    // }
    // if (this.interactivityManager) {
    //   this.interactivityManager.setSketchLayerIds(sketchLayerIds);
    // }

    return { style: baseStyle, sprites, sketchLayerIds };
  }

  resetToProjectBounds = () => {
    if (this.initialBounds && this.map) {
      this.map.fitBounds(this.initialBounds as LngLatBoundsLike, {
        animate: true,
      });
    }
  };

  computeSketchLayers() {
    const allLayers: AnyLayer[] = [];
    const sources: Sources = {};
    const sketchClassLayers: {
      [id: number]: { layers: string[]; sources: string[] };
    } = {};
    for (const stringId of this.sketchStates.keys()) {
      const id = parseInt(stringId);
      const sketchClassId = this.sketchStates.getRaw(stringId)?.sketchClassId;
      if (!sketchClassId) {
        throw new Error(
          `Sketch ${id} has no sketchClassId. This is required for sketch layers`
        );
      }
      if (!sketchClassLayers[sketchClassId]) {
        sketchClassLayers[sketchClassId] = {
          layers: [],
          sources: [],
        };
      }
      if (id !== this.hideEditableSketchId) {
        const timestamp = this.sketchTimestamps.get(id);
        const cache = LocalSketchGeometryCache.get(id);
        let layers = this.getLayersForSketch(
          id,
          id === this.editableSketchId,
          sketchClassId
        );
        if (
          sketchClassId &&
          this.internalState.sketchClassLayerStates[sketchClassId]
        ) {
          const state =
            this.internalState.sketchClassLayerStates[sketchClassId];
          if (state.opacity !== undefined && state.opacity < 1) {
            layers = adjustLayerOpacities(layers, state.opacity);
          }
        }
        if (
          layers.length > 0 &&
          "metadata" in layers[0] &&
          this.sketchStates.getRaw(stringId)?.filterMvtUrl
        ) {
          sources[`sketch-${id}`] = {
            type: "vector",
            tiles: [this.sketchStates.getRaw(stringId)!.filterMvtUrl!],
            maxzoom: 14,
          };
          allLayers.push(...layers);
          // eslint-disable-next-line i18next/no-literal-string
          sketchClassLayers[sketchClassId].sources.push(`sketch-${id}`);
          sketchClassLayers[sketchClassId].layers.push(
            ...layers.map((l) => l.id)
          );
        } else {
          sources[`sketch-${id}`] = {
            type: "geojson",
            data:
              cache && cache.timestamp === timestamp
                ? cache.feature
                : sketchGeoJSONUrl(id, timestamp),
          };
          if (this.editableSketchId && id !== this.editableSketchId) {
            reduceOpacity(layers);
          }
          allLayers.push(...layers);
          // eslint-disable-next-line i18next/no-literal-string
          sketchClassLayers[sketchClassId].sources.push(`sketch-${id}`);
          sketchClassLayers[sketchClassId].layers.push(
            ...layers.map((l) => l.id)
          );
        }
      }
    }
    return { layers: allLayers, sources, sketchClassLayers };
  }

  defaultSketchLayers = [
    {
      type: "fill",
      // Filter to type Polygon or Multipolygon
      filter: [
        "any",
        ["==", ["geometry-type"], "Polygon"],
        ["==", ["geometry-type"], "MultiPolygon"],
      ],
      paint: {
        "fill-color": "orange",
        "fill-outline-color": "red",
        "fill-opacity": 0.5,
      },
      layout: {},
    },

    {
      type: "line",
      filter: ["any", ["==", ["geometry-type"], "LineString"]],
      paint: {
        "line-color": "black",
        "line-width": 4,
      },
      layout: {},
    },
    {
      type: "line",
      filter: ["any", ["==", ["geometry-type"], "LineString"]],
      paint: {
        "line-color": "orange",
        "line-width": 2,
      },
      layout: {},
    },
    {
      type: "circle",
      filter: ["any", ["==", ["geometry-type"], "Point"]],
      paint: {
        "circle-radius": 5,
        "circle-color": "orange",
      },
    },
    {
      type: "circle",
      filter: ["any", ["==", ["geometry-type"], "Point"]],
      paint: {
        "circle-radius": 3,
        "circle-color": "white",
        "circle-opacity": 0.75,
      },
    },
  ] as AnyLayer[];

  assignSketchLayersToSketch(id: number, sourceId: string, layers: AnyLayer[]) {
    let layerIdCount = 0;
    return layers.map((lyr) => {
      return {
        ...lyr,
        source: sourceId,
        // eslint-disable-next-line i18next/no-literal-string
        id: `sketch-${id}-${layerIdCount++}`,
      };
    }) as AnyLayer[];
  }

  getLayersForSketch(
    id: number,
    focusOfEditing?: boolean,
    sketchClassId?: number
  ): AnyLayer[] {
    // eslint-disable-next-line i18next/no-literal-string
    const source = `sketch-${id}`;
    const layers = this.assignSketchLayersToSketch(
      id,
      source,
      this.getSketchClassGLStyles(sketchClassId || 99999)
    );
    if (
      (this.selectedSketches && this.selectedSketches.indexOf(id) !== -1) ||
      focusOfEditing
    ) {
      if (
        "metadata" in layers[0] &&
        layers[0].metadata?.["s:filterApiServerLocation"]?.length
      ) {
        const outline = {
          // eslint-disable-next-line i18next/no-literal-string
          id: `sketch-${id}-selection-outline`,
          type: "line",
          source,
          "source-layer": "cells",
          paint: {
            "line-color": "rgb(46, 115, 182)",
            "line-opacity": 0.7,
            "line-width": 1,
            "line-blur": 0,
            // "line-offset": -1,
          },
          // @ts-ignore
          // slot: "top",
          layout: {},
          // layout: { "line-join": "miter" },
        } as LineLayer;
        layers.push(outline);
      } else {
        layers.push(
          ...([
            {
              // eslint-disable-next-line i18next/no-literal-string
              id: `sketch-${id}-selection-second-outline`,
              type: "line",
              source,
              paint: {
                "line-color": "white",
                "line-opacity": 0.25,
                "line-width": 6,
                "line-blur": 0,
                "line-offset": -3,
              },
              // layout: { "line-join": "miter" },
            },
            {
              // eslint-disable-next-line i18next/no-literal-string
              id: `sketch-${id}-selection-outline`,
              type: "line",
              source,
              paint: {
                "line-color": "rgb(46, 115, 182)",
                "line-opacity": 1,
                "line-width": 2,
                "line-blur": 0,
                "line-offset": -1,
              },
              // layout: { "line-join": "miter" },
            },
          ] as AnyLayer[])
        );
      }
    }
    return layers;
  }

  // Source loading/error monitoring has been moved to LayerStateManager.
  // Each instance listens for map data/error events filtered by its
  // namespace prefix, with a fallback poll for stuck sources.

  private tocItems: {
    [stableId: string]: {
      stableId: string;
      id: number;
      label?: string;
      bounds?: [number, number, number, number];
      dataLayerId?: number;
      isFolder: boolean;
      parentStableId?: string;
      enableDownload: boolean;
      primaryDownloadUrl?: string;
      overlayDataTables?: ClientOverlayDataTableFragment[];
    };
  } = {};

  reset(
    sources: DataSourceDetailsFragment[],
    layers: DataLayerDetailsFragment[],
    tocItems: Pick<
      OverlayFragment,
      | "id"
      | "stableId"
      | "dataLayerId"
      | "geoprocessingReferenceId"
      | "title"
      | "bounds"
      | "isFolder"
      | "parentStableId"
      | "enableDownload"
      | "primaryDownloadUrl"
      | "overlayDataTables"
      | "acl"
    >[]
  ) {
    this.clientDataSources = {};
    for (const source of sources) {
      this.clientDataSources[source.id] = source;
    }
    const layersById: { [id: number]: DataLayerDetailsFragment } = {};
    for (const layer of layers) {
      layersById[layer.id] = layer;
      // this.layers[layer.id] = layer;
    }
    for (const item of tocItems) {
      this.tocItems[item.stableId] = {
        label: item.title,
        dataLayerId: item.dataLayerId || undefined,
        bounds: (item.bounds as [number, number, number, number]) || undefined,
        isFolder: Boolean(item.isFolder),
        parentStableId: item.parentStableId || undefined,
        stableId: item.stableId,
        id: item.id,
        enableDownload: Boolean(item.enableDownload),
        primaryDownloadUrl: item.primaryDownloadUrl || undefined,
        // Always store an array once the TOC item is in the catalog so sync
        // can distinguish "not loaded" (missing toc item) from "no tables".
        overlayDataTables: item.overlayDataTables || [],
      };
      if (item.dataLayerId) {
        const layer = layersById[item.dataLayerId];
        if (layer) {
          this.layers[item.stableId] = {
            ...layer,
            tocId: item.stableId,
          };
        }
        if (item.geoprocessingReferenceId) {
          this.setGeoprocessingReferenceId(
            item.geoprocessingReferenceId,
            item.stableId
          );
        }
      }
    }
    if (Object.keys(layers).length) {
      // Cleanup entries in overlayStates that no longer exist
      const validKeys = new Set(tocItems.map((i) => i.stableId));
      this.overlayStates.retainOnly(validKeys);
      for (const id of [...this.dataTableActiveTocIds]) {
        if (!validKeys.has(id)) {
          this.dataTableActiveTocIds.delete(id);
        }
      }
      for (const stableId of this.overlayStates.keys()) {
        const layer = this.layers[stableId];
        if (layer) {
          this.overlayStates.setSourceForKey(
            stableId,
            this.overlayStates.prefixedSourceId(layer.dataSourceId)
          );
        }
        // Rebuild active set from restored LayerState (e.g. preferences).
        if (this.overlayStates.getRaw(stableId)?.dataTable?.stableId) {
          this.dataTableActiveTocIds.add(stableId);
        }
      }
      this.deactivateOtherDataTables();
      this.debouncedUpdatePreferences();
    }
    // TOC catalog (including overlayDataTables) may have just arrived or
    // changed — resolve LayerState.dataTable and (re)start queries.
    this.scheduleDataTableFeatureStateSync();
    this.debouncedUpdateStyle();
    this.updateLegends();
    return;
  }

  hideTocItems(stableIds: string[]) {
    for (const id of stableIds) {
      this.hideTocItem(id);
    }
    this.debouncedUpdateStyle();
  }

  /**
   * Update the map clock used for data-table queries. Instant clocks share
   * one cached `when.step` series; window clocks query the selected range.
   * Painting coalesces to one animation frame so dragging can scrub live.
   */
  setTemporalClock(clock: TemporalClock | null) {
    this.dataTableQueryManager.setTemporalClock(clock);
    if (this.dataTableClockRaf != null) {
      return;
    }
    this.dataTableClockRaf = requestAnimationFrame(() => {
      this.dataTableClockRaf = null;
      this.syncDataTableFeatureStates();
    });
  }

  setOnDataTableSeriesCountsChange(
    callback:
      | ((counts: {
          [tableStableId: string]: { [step: string]: number };
        }) => void)
      | null
  ) {
    this.dataTableQueryManager.setOnSeriesCountsChange(callback);
  }

  getDataTableSeriesCounts(): {
    [tableStableId: string]: { [step: string]: number };
  } {
    return this.dataTableQueryManager.getSeriesCounts();
  }

  setOnDataTableSeriesCountsLoadingChange(
    callback: ((loading: boolean) => void) | null
  ) {
    this.dataTableQueryManager.setOnSeriesCountsLoadingChange(callback);
  }

  getDataTableSeriesCountsLoading(): boolean {
    return this.dataTableQueryManager.getSeriesCountsLoading();
  }

  setOnDataTableQueryErrorsChange(
    callback: ((errors: string[]) => void) | null
  ) {
    this.dataTableQueryManager.setOnQueryErrorsChange(callback);
  }

  getDataTableQueryErrors(): string[] {
    return this.dataTableQueryManager.getQueryErrors();
  }

  setTemporallyFilteredTocItems(stableIds: string[]) {
    if (
      this.temporallyHiddenTocIds.size === stableIds.length &&
      stableIds.every((id) => this.temporallyHiddenTocIds.has(id))
    ) {
      return;
    }
    this.temporallyHiddenTocIds = new Set(stableIds);
    // Range inputs can emit far faster than a whole-style update can finish.
    // Preserve the declarative style path, but wait briefly for dragging to
    // settle. Playback ticks are much farther apart than this delay.
    this.debouncedUpdateStyle(50);
  }

  /** Legend-eye hide — omit the layer from the computed style. */
  private isLegendEyeHidden(layerId: string): boolean {
    return Boolean(this.overlayStates.getRaw(layerId)?.hidden);
  }

  showTocItems(stableIds: string[]) {
    for (const id of stableIds) {
      this.showTocItem(id);
    }
    this.debouncedUpdateStyle();
  }

  private onLockReleaseRequested:
    | ((requester: string, state: DigitizingLockState) => Promise<Boolean>)
    | null = null;

  /**
   * Used to manage conflicts between different digitizing tools. Tools can
   * request a DigitizingLockState change. False is returned if the requested
   * state is not available. Tools should provide a callback function that can
   * release the lock if requested by other tools, cleaning up their internal
   * state before doing so.
   *
   * @param id Unique identifier for the tool requesting the lock, such as "Sketching" or "MeasureControl"
   * @param state CursorActive or Editing
   * @param onReleaseRequested Callback function that can release the lock
   */
  async requestDigitizingLock(
    id: string,
    state: DigitizingLockState.CursorActive | DigitizingLockState.Editing,
    onReleaseRequested: (
      requester: string,
      state: DigitizingLockState
    ) => Promise<Boolean>
  ) {
    if (this.internalState.digitizingLockState === DigitizingLockState.Free) {
      // can go ahead and activate tool
      this.setDigitizingLockState(state, id);
      this.onLockReleaseRequested = onReleaseRequested;
      return true;
    } else {
      // first, check if you can release the lock
      const released =
        this.onLockReleaseRequested === null ||
        id === this.internalState.digitizingLockedBy
          ? true
          : await this.onLockReleaseRequested(
              id,
              this.internalState.digitizingLockState
            );
      if (released) {
        this.onLockReleaseRequested = onReleaseRequested;
        this.setDigitizingLockState(state, id);
        // activate tool
        return true;
      } else {
        // notify tool that it can't be activated
        return false;
      }
    }
  }

  /**
   * Used to release a digitizing lock. If the lock is not held by the tool
   * requesting the release, nothing happens.
   * @param id Unique identifier for the tool requesting the release
   */
  releaseDigitizingLock(id: string) {
    if (this.internalState.digitizingLockState !== DigitizingLockState.Free) {
      if (this.internalState.digitizingLockedBy === id) {
        this.setDigitizingLockState(DigitizingLockState.Free);
        this.onLockReleaseRequested = null;
      } else {
        // TODO: Should I throw an error or do nothing here?
      }
    }
  }

  /**
   * Used to release a digitizing lock. If the lock is not held by the tool
   * requesting the release, nothing happens.
   * @param id Unique identifier for the tool requesting the release
   * @param state State to set the lock to
   */
  private setDigitizingLockState(state: DigitizingLockState, id?: string) {
    if (!id && state !== DigitizingLockState.Free) {
      throw new Error("Must provide id when setting non-free state");
    }
    this.setState((prev) => ({
      ...prev,
      digitizingLockState: state,
      digitizingLockedBy: id || undefined,
    }));
    this.emit(DigitizingLockStateChangeEventType, {
      digitizingLockState: state,
      digitizingLockedBy: id || undefined,
    });
    if (state === DigitizingLockState.CursorActive) {
      this.emit("pauseInteractivity");
    } else {
      this.emit("resumeInteractivity");
    }
  }

  hasLock(id: string) {
    return (
      this.internalState.digitizingLockState !== DigitizingLockState.Free &&
      this.internalState.digitizingLockedBy === id
    );
  }

  private spritesById: { [id: string]: SpriteDetailsFragment } = {};

  private async addSprites(
    sprites: SpriteDetailsFragment[],
    map: mapboxgl.Map
  ) {
    // get unique sprite ids
    for (const sprite of sprites) {
      const spriteId =
        typeof sprite.id === "string"
          ? sprite.id
          : // eslint-disable-next-line
            `seasketch://sprites/${sprite.id}`;
      this.spritesById[spriteId] = sprite;
      // if (!map!.hasImage(spriteId)) {
      //   this.addSprite(sprite, map);
      // }
    }
  }

  private async addSprite(sprite: SpriteDetailsFragment, map: mapboxgl.Map) {
    let spriteImage = sprite.spriteImages.find(
      (i) => window.devicePixelRatio === i.pixelRatio
    );
    if (!spriteImage) {
      spriteImage = sprite.spriteImages[0];
    }
    const spriteId =
      typeof sprite.id === "string"
        ? sprite.id
        : // eslint-disable-next-line
          `seasketch://sprites/${sprite.id}`;

    // TODO: Okay to remove?
    // if (spriteImage.dataUri) {
    //   const image = await createImage(
    //     spriteImage.width,
    //     spriteImage.height,
    //     spriteImage.dataUri
    //   );
    //   this.map?.addImage(spriteId, image, {
    //     pixelRatio: spriteImage.pixelRatio,
    //   });
    // } else if (spriteImage.url) {
    const image = await loadImage(spriteImage.url, map);
    map.addImage(spriteId, image, {
      pixelRatio: spriteImage.pixelRatio,
    });
    // } else {
    //   /* eslint-disable-next-line */
    //   throw new Error(`Sprite id=${sprite.id} missing both dataUri and url`);
    // }
  }

  onStyleImageMissing = (e: any) => {
    if (/seasketch:\/\/sprites\/\d+/.test(e.id)) {
      const spriteFragment = this.spritesById[e.id];
      if (spriteFragment) {
        this.addSprite(spriteFragment, this.map!);
      }
    }
  };

  /**
   * Called when `overlayStates` emits `"stateChanged"`.
   * Pushes overlay state (and iNaturalist CTAs) to React contexts.
   * Does NOT push sketch state — overlay loading churn should not cause
   * SketchLayerContext consumers to re-render.
   */
  private onOverlayStateChanged = (state: { [key: string]: LayerState }) => {
    this.setState((oldState) => ({
      ...oldState,
      layerStatesByTocStaticId: state,
    }));
    // Only emit uiUpdate when the iNaturalist CTAs actually changed.
    const nextCtas = this.computeInatCtas();
    if (!this.inatCtasEqual(this._lastInatCtas, nextCtas)) {
      this._lastInatCtas = nextCtas;
      this.emit("uiUpdate", {
        inaturalistCallToActions: nextCtas,
      } as Partial<MapUIStateContextState>);
    }
    this.debouncedUpdatePreferences();
  };

  /**
   * Called when `sketchStates` emits `"stateChanged"`.
   * Receives the effective state directly from the event payload and pushes
   * it to the SketchLayerContext without re-calling getState().
   */
  private onSketchStateChanged = (state: {
    [key: string]: SketchLayerState;
  }) => {
    const sketchLayerStates: { [id: number]: SketchLayerState } = {};
    for (const key of Object.keys(state)) {
      sketchLayerStates[parseInt(key)] = state[key];
    }
    this._setSketchLayerState?.({
      sketchLayerStates,
      sketchClassLayerStates: {
        ...this.internalState.sketchClassLayerStates,
      },
    });
    this.debouncedUpdatePreferences();
  };

  /** Shallow-compare two iNaturalist CTA arrays by projectId + label. */
  private inatCtasEqual(
    a: { projectId: string; label?: string }[],
    b: { projectId: string; label?: string }[]
  ): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i].projectId !== b[i].projectId || a[i].label !== b[i].label)
        return false;
    }
    return true;
  }

  setCamera(camera: CameraOptions) {
    if (this.map) {
      if (camera.center) {
        this.map.setCenter(camera.center);
      }
      if (camera.zoom) {
        this.map.setZoom(camera.zoom);
      }
      if (camera.pitch) {
        this.map.setPitch(camera.pitch);
      }
      if (camera.bearing) {
        this.map.setBearing(camera.bearing);
      }
    }
  }

  private dynamicDataSources: { [id: string]: AnySourceData } = {};
  private dynamicLayers: AnyLayer[] = [];

  /**
   * Add a data source directly to the map. Don't use the direct mapbox-gl
   * methods or your source will disappear whenever the manager resets the map
   * style using the product of getComputedStyle().
   * @param id
   * @param source
   */
  addSource(id: string, source: AnySourceData) {
    this.dynamicDataSources[id] = source;
    this.debouncedUpdateStyle();
  }

  removeSource(id: string) {
    delete this.dynamicDataSources[id];
    this.debouncedUpdateStyle();
  }

  /**
   * 
   * Add a layer directly to the map. Don't use the direct mapbox-gl 
   * methods or your source will disappear whenever the manager resets the map
   * style using the product of getComputedStyle().

   * @param layer 
   */
  addLayer(layer: AnyLayer) {
    const idx = this.dynamicLayers.findIndex((l) => l.id === layer.id);
    if (idx > -1) {
      this.dynamicLayers[idx] = layer;
    } else {
      this.dynamicLayers.push(layer);
    }
    this.debouncedUpdateStyle();
  }

  removeLayer(id: string) {
    this.dynamicLayers = this.dynamicLayers.filter((lyr) => lyr.id !== id);
    this.debouncedUpdateStyle();
  }

  async enableOfflineTileSimulator(settings: OfflineTileSettings) {
    const enabled = await ServiceWorkerWindow.enableOfflineTileSimulator(
      settings
    );
    if (!enabled) {
      throw new Error("Invalid response from service worker");
    }
    this.setState((prev) => ({ ...prev, offlineTileSimulatorActive: true }));
    this.emit("uiUpdate", {
      offlineTileSimulatorActive: true,
    } as Partial<MapUIStateContextState>);
    this.updateStyle();
    // Using a bunch of undocumented private apis here to reset tile caches.
    // See:
    // https://github.com/mapbox/mapbox-gl-js/issues/2633#issuecomment-576050636
    // @ts-ignore
    const sourceCaches = this.map?.style?._sourceCaches;
    await window.caches.delete("mapbox-tiles");
    if (sourceCaches) {
      for (const cache of Object.values(sourceCaches) as any[]) {
        for (const id in cache._tiles) {
          cache._tiles[id].expirationTime = Date.now() - 1;
          cache._reloadTile(id, "reloading");
        }
        cache._cache.reset();
      }
      this.map?.triggerRepaint();
      this.map?.resize();
      this.map?.triggerRepaint();
      const center = this.map!.getCenter()!;
      center.lat = center.lat * 1.0000001;
      this.map!.setCenter(center);
      this.map?.triggerRepaint();
    }
  }

  async disableOfflineTileSimulator() {
    const enabled = await ServiceWorkerWindow.disableOfflineTileSimulator();
    if (enabled) {
      throw new Error("Invalid response from service worker");
    }
    this.setState((prev) => ({ ...prev, offlineTileSimulatorActive: false }));
    this.emit("uiUpdate", {
      offlineTileSimulatorActive: false,
    } as Partial<MapUIStateContextState>);
    this.updateStyle();
  }

  async getMapBookmarkData(skipThumbnail = false) {
    if (!this.map) {
      throw new Error("Map not ready to create bookmark data");
    }
    const visibleDataLayers: string[] = [];
    for (const stableId of this.overlayStates.keys()) {
      if (
        this.overlayStates.getRaw(stableId)?.visible &&
        this.layers[stableId]
      ) {
        visibleDataLayers.push(stableId);
      }
    }
    const visibleSketches: number[] = [];
    for (const key of this.sketchStates.keys()) {
      if (this.sketchStates.getRaw(key)?.visible) {
        visibleSketches.push(parseInt(key));
      }
    }
    const canvas = this.map.getCanvas();
    const sidebarState = currentSidebarState();
    const style = this.map.getStyle();

    const clientGeneratedThumbnail = skipThumbnail
      ? undefined
      : await this.getMapThumbnail(sidebarState);
    const selectedId = this.basemapState?.selectedBasemap;
    const dataTableStates = buildDataTableStatesFromLayers(
      this.overlayStates.getState()
    );
    return {
      cameraOptions: {
        center: this.map.getCenter().toArray(),
        zoom: this.map.getZoom(),
        bearing: this.map.getBearing(),
        pitch: this.map.getPitch(),
      },
      basemapOptionalLayerStates:
        this.basemapState?.basemapOptionalLayerStates || {},
      visibleDataLayers,
      selectedBasemap: selectedId ?? 0,
      style,
      mapDimensions: [canvas.clientWidth, canvas.clientHeight],
      sidebarState,
      visibleSketches,
      basemapName: selectedId ? this.basemaps[selectedId]?.name || "" : "",
      clientGeneratedThumbnail,
      dataTableStates,
    };
  }

  /**
   * Wait briefly for in-flight data-table queries so thumbnails aren't gray.
   * Always resolves so bookmark creation is not blocked.
   */
  private async waitForDataTableQueriesIdle(timeoutMs = 2500) {
    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
      const loading = Object.values(this.activatedDataTableLoading).some(
        Boolean
      );
      if (!loading) {
        return;
      }
      await new Promise((r) => setTimeout(r, 50));
    }
  }

  /**
   * Re-apply in-memory data-table feature-state onto a map (e.g. thumbnail
   * clone). Style paint uses feature-state which is not part of getStyle().
   */
  private applyActivatedDataTableFeatureStatesToMap(targetMap: Map) {
    for (const tocStableId of Object.keys(this.activatedDataTableValues)) {
      const values = this.activatedDataTableValues[tocStableId]?.values;
      const activation =
        this.resolveDataTableVisualizationSettings(tocStableId);
      if (!values || !activation) {
        continue;
      }
      const layer = this.layers[tocStableId];
      const table = activation.table;
      if (!layer || !table.overlayJoinColumn || !table.joinColumn) {
        continue;
      }
      const sourceId = this.overlayStates.prefixedSourceId(layer.dataSourceId);
      if (!targetMap.getSource(sourceId)) {
        continue;
      }
      const columnStatsUrl = columnStatsUrlForTable(table);
      const columnStats = columnStatsUrl
        ? getCachedDataTableColumnStats(columnStatsUrl)
        : undefined;
      const joinIds = this.getJoinFeatureIdsFromColumnStats(
        columnStats,
        table.joinColumn
      );
      const allFeatureIds = joinIds.length > 0 ? joinIds : Object.keys(values);
      for (const featureId of allFeatureIds) {
        const stateValue =
          values[featureId] !== undefined
            ? values[featureId]
            : DATA_TABLE_NO_DATA_VALUE;
        targetMap.setFeatureState(
          {
            source: sourceId,
            id: featureId,
            ...(layer.sourceLayer ? { sourceLayer: layer.sourceLayer } : {}),
          },
          { [DATA_TABLE_VALUE_PROPERTY]: stateValue }
        );
      }
    }
  }

  getMapThumbnail = withTimeout(
    15000,
    async (sidebarState: {
      isSmall: boolean;
      open: boolean;
      width: number;
    }) => {
      return new Promise(async (resolve, reject) => {
        try {
          if (!this.map) {
            throw new Error("Map not ready to create thumbnail");
          }
          await this.waitForDataTableQueriesIdle();
          const { sprites } = await this.getComputedStyle();
          const style = this.map.getStyle();
          const div = document.createElement("div");
          div.style.setProperty("position", "absolute");
          const width = this.map.getContainer().clientWidth;
          const height = this.map.getContainer().clientHeight;
          div.style.setProperty("left", width * -1.1 + "px");
          div.style.setProperty("top", "0px");
          // div.style.setProperty("z-index", "9999999");
          div.style.setProperty("width", width + "px");
          div.style.setProperty("height", height + "px");
          document.body.appendChild(div);
          const newMap = new mapboxgl.Map({
            style,
            container: div,
            preserveDrawingBuffer: true,
            center: this.map.getCenter(),
            zoom: this.map.getZoom(),
            transformRequest: this.requestTransformer,
          });
          this.addSprites(sprites, newMap);
          let captured = false;
          const capture = () => {
            if (captured) {
              return;
            }
            captured = true;
            let clip: null | { x: number; width: number } = null;
            if (sidebarState.open && width >= 1080) {
              clip = {
                width: width - sidebarState.width,
                x: sidebarState.width,
              };
            }
            const targetWidth = 240;
            const scalingFactor = targetWidth / width;
            const targetHeight = scalingFactor * height;
            const canvas = newMap.getCanvas();
            const resizedCanvas = document.createElement("canvas");
            const oc = document.createElement("canvas");
            const octx = oc.getContext("2d");
            if (!octx) {
              return reject(new Error("Could not create canvas context"));
            }
            oc.width = clip ? canvas.width - clip.x : canvas.width;
            oc.height = canvas.height;

            // step 2: pre-filter image using steps as radius
            const steps = (oc.width / targetWidth) >> 1;
            // eslint-disable-next-line i18next/no-literal-string
            octx.filter = `blur(${steps}px)`;
            octx.drawImage(canvas, clip ? clip.x * -1 : 0, 0);
            // Should be half the size of the thumbnail variant in cloudflare images
            resizedCanvas.width = targetWidth;
            resizedCanvas.height = targetHeight;
            const resizedContext = resizedCanvas.getContext("2d");
            if (!resizedContext) {
              return reject(new Error("Could not create canvas context"));
            }
            resizedContext.drawImage(oc, 0, 0, targetWidth, targetHeight);
            const data = resizedCanvas.toDataURL("image/jpeg", 0.7);
            newMap.remove();
            div.remove();
            return resolve(data);
          };
          newMap.on("load", () => {
            this.applyActivatedDataTableFeatureStatesToMap(newMap);
            // Wait for feature-state paint to commit before reading the canvas.
            newMap.once("idle", () => {
              capture();
            });
            // Fallback if idle never fires (e.g. no tiles for some sources).
            setTimeout(() => {
              capture();
            }, 2000);
          });
        } catch (e) {
          reject(e);
        }
      });
    }
  );

  async showMapBookmark(
    bookmark: Pick<
      MapBookmarkDetailsFragment,
      | "cameraOptions"
      | "basemapOptionalLayerStates"
      | "visibleDataLayers"
      | "selectedBasemap"
      | "dataTableStates"
    > & { id?: string },
    savePreviousState = true,
    client?: ApolloClient<NormalizedCacheObject>
  ) {
    if (savePreviousState) {
      this.previousMapState = await this.getMapBookmarkData(true);
    }
    if (!this.map) {
      throw new Error("Map not ready to show bookmark");
    }
    if (!this.basemapState) {
      throw new Error("BasemapContext not available when showing bookmark");
    }
    this.basemapState.setSelectedBasemap(bookmark.selectedBasemap);
    if (bookmark.basemapOptionalLayerStates) {
      const basemap = this.basemaps[bookmark.selectedBasemap];
      for (const [key, value] of Object.entries(
        bookmark.basemapOptionalLayerStates
      )) {
        const layer = basemap?.optionalBasemapLayers.find(
          (l) => l.name === key
        );
        if (layer) {
          this.basemapState.updateOptionalBasemapSetting(
            layer,
            value as OptionalBasemapLayerValue
          );
        }
      }
    }
    this.setVisibleTocItems((bookmark.visibleDataLayers || []) as string[]);
    const dataTableStates =
      (bookmark.dataTableStates as DataTableStatesMap | null | undefined) || {};
    this.applyDataTableStates(dataTableStates);
    this.map.flyTo({
      ...bookmark.cameraOptions,
      animate: true,
    });
    if (savePreviousState && bookmark.id) {
      this.emit("uiUpdate", {
        displayedMapBookmark: {
          id: bookmark.id!,
          errors: this.getErrorsForBookmark(bookmark, client),
          supportsUndo: true,
        },
      } as Partial<MapUIStateContextState>);
      this.hideBookmarkBanner(6000);
    }
  }

  getErrorsForBookmark(
    bookmark: Pick<
      MapBookmarkDetailsFragment,
      "selectedBasemap" | "visibleSketches" | "visibleDataLayers"
    >,
    client?: ApolloClient<NormalizedCacheObject>
  ) {
    const missingSketches: number[] = [];
    for (const id of bookmark.visibleSketches || []) {
      if (
        id &&
        client &&
        !client.readFragment({
          fragment: SketchPresentFragmentDoc,
          // eslint-disable-next-line i18next/no-literal-string
          id: `Sketch:${id}`,
        })
      ) {
        missingSketches.push(id);
      }
    }
    return {
      missingLayers: this.getMissingLayers(
        (bookmark.visibleDataLayers || []) as string[]
      ),
      missingBasemap: this.basemaps[bookmark.selectedBasemap] ? false : true,
      missingSketches,
    };
  }

  async undoMapBookmark() {
    if (this.previousMapState) {
      this.showMapBookmark(this.previousMapState, false);
      this.hideBookmarkBanner();
      this.previousMapState = undefined;
    }
  }

  bookmarkBannerHideTimeout?: any;

  /**
   *
   * @param delay in milliseconds
   */
  hideBookmarkBanner(delay?: number) {
    if (this.bookmarkBannerHideTimeout) {
      clearTimeout(this.bookmarkBannerHideTimeout);
    }
    if (delay) {
      this.bookmarkBannerHideTimeout = setTimeout(() => {
        this.emit("uiUpdate", {
          displayedMapBookmark: undefined,
        } as Partial<MapUIStateContextState>);
        this.bookmarkBannerHideTimeout = undefined;
      }, delay);
    } else {
      this.emit("uiUpdate", {
        displayedMapBookmark: undefined,
      } as Partial<MapUIStateContextState>);
    }
  }

  cancelBookmarkBannerHiding() {
    if (this.bookmarkBannerHideTimeout) {
      clearTimeout(this.bookmarkBannerHideTimeout);
    }
  }

  /**
   * Given a list of layer ids, return how many are unknown to MapContextManager
   */
  getMissingLayers(layerIds: string[]) {
    const missing: string[] = [];
    const knownIds = Object.keys(this.layers);
    const knownStaticIds = Object.keys(this.layers).reduce((prev, key) => {
      const staticId = this.layers[key].staticId;
      if (staticId) {
        prev.push(staticId);
      }
      return prev;
    }, [] as string[]);
    for (const id of layerIds) {
      if (knownIds.indexOf(id) === -1 && knownStaticIds.indexOf(id) === -1) {
        missing.push(id);
      }
    }
    return missing;
  }

  getMissingSketches() {}

  isBasemapMissing() {}

  private async _updateLegends(clearCache = false) {
    const newLegendState: { [layerId: string]: LegendItem | null } = {};
    let changes = false;
    const layers = this.getVisibleLayersByZIndex();
    for (const layer of layers) {
      if (layer.sketchClassLayerState) {
        // add visible sketchClass layer legends
        const sketchClassLayerState = layer.sketchClassLayerState;
        if (sketchClassLayerState.visible && sketchClassLayerState.legendItem) {
          newLegendState[sketchClassLayerState.legendItem.id] =
            sketchClassLayerState.legendItem;
          changes = true;
        }
      } else if (
        layer.dataLayer?.tocId &&
        this.overlayStates.getRaw(layer.dataLayer.tocId)?.visible
      ) {
        const id = layer.dataLayer.tocId;
        if (clearCache === true && id in this.internalState.legends) {
          newLegendState[id] = this.internalState.legends[id];
        } else {
          const tableOfContentsItemDetails: TocMenuItemType = {
            stableId: id,
            enableDownload: Boolean(this.tocItems[id]?.enableDownload),
            isFolder: Boolean(this.tocItems[id]?.isFolder),
            id: this.tocItems[id].id,
            title: this.tocItems[id].label || "Unknown",
            primaryDownloadUrl: this.tocItems[id]?.primaryDownloadUrl,
          };
          const overlayDataTables = this.tocItems[id]?.overlayDataTables;
          const layer = this.layers[id];
          const source = this.clientDataSources[layer?.dataSourceId];
          if (layer && source && layer.mapboxGlStyles) {
            let sourceType:
              | undefined
              | "vector"
              | "raster"
              | "image"
              | "video"
              | "raster-dem"
              | "geojson" = undefined;
            switch (source.type) {
              case DataSourceTypes.Geojson:
              case DataSourceTypes.SeasketchVector:
                sourceType = "geojson";
                break;
              case DataSourceTypes.Raster:
              case DataSourceTypes.SeasketchRaster:
                sourceType = "raster";
                break;
              case DataSourceTypes.Vector:
              case DataSourceTypes.SeasketchMvt:
                sourceType = "vector";
                break;
              case DataSourceTypes.RasterDem:
                sourceType = "raster-dem";
                break;
              case DataSourceTypes.ArcgisDynamicMapserver:
              case DataSourceTypes.Image:
                sourceType = "image";
                break;
              case DataSourceTypes.Video:
                sourceType = "video";
                break;
            }

            if (sourceType) {
              try {
                const respectRasterOffsetAndScale = Boolean(
                  (layer.mapboxGlStyles || []).find(
                    (l: any) => l.metadata?.["s:respect-scale-and-offset"]
                  )
                );
                const dataTableActivation =
                  this.resolveDataTableVisualizationSettings(id);
                const dataTableOp = dataTableActivation?.query.op
                  ? Array.isArray(dataTableActivation.query.op)
                    ? dataTableActivation.query.op[0]
                    : dataTableActivation.query.op
                  : undefined;
                // Show the data-table legend panel as soon as a table is
                // activated — even before a column is resolved. Column/op
                // controls live in that panel and pick a default numeric
                // column from column-stats when admin constraints are empty.
                if (dataTableActivation && dataTableActivation.table.stableId) {
                  const sourceId = this.overlayStates.prefixedSourceId(
                    layer.dataSourceId
                  );
                  const legendSummary =
                    this.dataTableQueryManager.getLegendSummary(sourceId);
                  newLegendState[id] = {
                    id,
                    type: "DataTableLegendItem",
                    label: this.tocItems[layer.tocId]?.label || "",
                    tableOfContentsItemDetails,
                    overlayDataTables,
                    loading: legendSummary?.loading ?? true,
                    error: legendSummary?.error,
                    tableStableId: dataTableActivation.table.stableId,
                    tableName: dataTableActivation.table.name,
                    column: dataTableActivation.query.column,
                    op: (dataTableOp || "mean") as DataTableAggregation,
                    min:
                      legendSummary &&
                      legendSummary.scaleMax > legendSummary.scaleMin
                        ? legendSummary.scaleMin
                        : legendSummary?.scaleMax ?? 0,
                    max:
                      legendSummary &&
                      legendSummary.scaleMax > legendSummary.scaleMin
                        ? legendSummary.scaleMax
                        : legendSummary?.scaleMax ?? 1,
                    hasZero: legendSummary?.hasZero ?? false,
                    // Show the bubble scale for any positive values, including
                    // a single unique value where scaleMin === scaleMax.
                    // Keep it during refetch (loading) so the legend dims the
                    // stale scale instead of swapping layouts.
                    showValueScale: (legendSummary?.scaleMax ?? 0) > 0,
                  };
                  changes = true;
                } else {
                  const dataTableLayer = this.dataTableCircleLayer(
                    id,
                    layer,
                    this.overlayStates.prefixedSourceId(layer.dataSourceId),
                    layer.sourceLayer || undefined,
                    true
                  );
                  const legend = compileLegendFromGLStyleLayers(
                    dataTableLayer ? [dataTableLayer] : layer.mapboxGlStyles,
                    sourceType,
                    source.rasterRepresentativeColors,
                    respectRasterOffsetAndScale
                      ? (source.rasterScale as number | undefined)
                      : undefined,
                    respectRasterOffsetAndScale
                      ? (source.rasterOffset as number | undefined)
                      : undefined
                  );
                  if (legend) {
                    newLegendState[id] = {
                      id,
                      type: "GLStyleLegendItem",
                      legend: legend,
                      label: this.tocItems[layer.tocId]?.label || "",
                      tableOfContentsItemDetails,
                      overlayDataTables,
                    };
                    changes = true;
                  } else {
                    newLegendState[id] = null;
                    changes = true;
                  }
                }
              } catch (e) {
                console.error(e);
                newLegendState[id] = {
                  id,
                  type: "GLStyleLegendItem",
                  label: this.tocItems[layer.tocId]?.label || "",
                  tableOfContentsItemDetails,
                  overlayDataTables,
                };
                changes = true;
              }
            }
          } else if (source && source.type === DataSourceTypes.Inaturalist) {
            const params = normalizeInaturalistParams(
              (source.queryParameters as any) || {}
            );
            const modes =
              params.type === "grid+points"
                ? {
                    kind: "split" as const,
                    below: "grid" as const,
                    above: "points" as const,
                    cutoff: params.zoomCutoff,
                  }
                : params.type === "heatmap+points"
                ? {
                    kind: "split" as const,
                    below: "heatmap" as const,
                    above: "points" as const,
                    cutoff: params.zoomCutoff,
                  }
                : {
                    kind: "single" as const,
                    layerType:
                      params.type === "grid"
                        ? ("grid" as const)
                        : params.type === "heatmap"
                        ? ("heatmap" as const)
                        : ("points" as const),
                  };

            newLegendState[id] = {
              id,
              type: "InaturalistLegendItem",
              label: this.tocItems[layer.tocId]?.label || "",
              modes,
              tableOfContentsItemDetails,
              overlayDataTables,
            } as LegendItem;
            changes = true;
          } else if (source && isCustomSourceType(source.type)) {
            const d = this.customSources[source.id];
            if (d && d.visible && d.customSource) {
              const { customSource } = d;
              const { tableOfContentsItems } =
                await customSource.getComputedMetadata();
              const item =
                tableOfContentsItems.length === 1
                  ? tableOfContentsItems[0]
                  : tableOfContentsItems.find(
                      (i) => i.id.toString() === layer?.sublayer?.toString()
                    );
              if (item && item.type === "data") {
                if (item.glStyle) {
                  newLegendState[id] = {
                    id,
                    type: "GLStyleLegendItem",
                    legend: compileLegendFromGLStyleLayers(
                      item.glStyle.layers,
                      "vector"
                    ),
                    label: this.tocItems[layer.tocId]?.label || "",
                    tableOfContentsItemDetails,
                    overlayDataTables,
                  };
                } else if (item.legend) {
                  newLegendState[id] = {
                    type: "CustomGLSourceSymbolLegend",
                    label: this.tocItems[layer.tocId]?.label || item.label,
                    supportsDynamicRendering: {
                      layerOpacity: true,
                      layerOrder: true,
                      layerVisibility: true,
                    },
                    symbols: item.legend,
                    id,
                    tableOfContentsItemDetails,
                    overlayDataTables,
                  };
                }
                changes = true;
              }
            }
          }
        }
      } else {
        const id = layer.dataLayer?.tocId || layer.id;
        if (id && id in this.internalState.legends) {
          delete newLegendState[id];
          changes = true;
        }
      }
    }

    if (
      changes ||
      Object.keys(newLegendState).length !==
        Object.keys(this.internalState.legends).length
    ) {
      this.setState((prev) => ({
        ...prev,
        legends: newLegendState,
      }));
    }
  }

  updateLegends = debounce(this._updateLegends, 20, {
    maxWait: 1000,
  });

  hideLayer(stableId: string) {
    this.overlayStates.setHidden(stableId, true);
    this.updateStyle();
  }

  showHiddenLayer(stableId: string) {
    this.overlayStates.setHidden(stableId, false);
    this.updateStyle();
  }

  private setZOrderOverride(
    stableId: string,
    zOrder: number,
    isSketchClassLayer = false
  ) {
    if (isSketchClassLayer) {
      const id = parseInt(stableId.replace("sketch-class-", ""));
      const state = this.internalState.sketchClassLayerStates[id];
      if (state) {
        state.zOrderOverride = zOrder;
        this.pushSketchState();
        this.debouncedUpdatePreferences();
      }
    } else {
      this.overlayStates.setZOrderOverride(stableId, zOrder);
    }
    // this.resetLayersByZIndex();
    this.updateStyle();
  }

  getVisibleLayersByZIndex() {
    const visibleLayers = Object.values(this.layers).filter(
      (l) => this.overlayStates.getRaw(l.tocId)?.visible === true
    );
    const visibleSketchClassLayers = Object.values(
      this.internalState.sketchClassLayerStates
    ).filter((l) => l.visible === true);

    // First, create a lookup table of the lowest sublayer z-index for each
    // datasource. This way sublayers are grouped together in the listing
    const sublayerZIndexLookup: {
      [dataSourceId: number]: number;
    } = {};
    for (const layer of visibleLayers) {
      if (layer.sublayer !== undefined && layer.sublayer !== null) {
        const dataSourceId = layer.dataSourceId;
        const zIndex =
          typeof this.overlayStates.getRaw(layer.tocId)?.zOrderOverride ===
          "number"
            ? this.overlayStates.getRaw(layer.tocId)!.zOrderOverride!
            : layer.zIndex;
        if (
          !(dataSourceId in sublayerZIndexLookup) ||
          zIndex < sublayerZIndexLookup[dataSourceId]
        ) {
          sublayerZIndexLookup[dataSourceId] = zIndex;
        }
      }
    }

    const getZIndexOverride = (
      layer: (typeof visibleLayers)[0] | (typeof visibleSketchClassLayers)[0]
    ) => {
      if (isSketchClassLayerState(layer)) {
        return layer.zOrderOverride || undefined;
      } else {
        return (
          this.overlayStates.getRaw(layer.tocId)?.zOrderOverride || undefined
        );
      }
    };

    const getZIndex = (
      layer: (typeof visibleLayers)[0] | (typeof visibleSketchClassLayers)[0],
      sameDataSource = false
    ) => {
      const zIndexOverride = getZIndexOverride(layer);
      if (isSketchClassLayerState(layer)) {
        // For sketch class layers, use the zIndex from the state
        if (typeof zIndexOverride === "number") {
          return zIndexOverride;
        }
        return isPointLayer(layer.styles) ? -2 : -1;
      } else {
        if (!sameDataSource && layer.dataSourceId in sublayerZIndexLookup) {
          return sublayerZIndexLookup[layer.dataSourceId];
        } else if (typeof zIndexOverride === "number") {
          return zIndexOverride;
        } else {
          return isPointLayer(layer.mapboxGlStyles)
            ? (layer.zIndex || 1) * -1 - 1000
            : layer.zIndex;
        }
      }
    };

    return [...visibleSketchClassLayers, ...visibleLayers]
      .sort((a, b) => {
        // cb - 4/29/2025 - removed renderunder checks. It just doesn't seem
        // that useful and obfuscates the logic such that I'm having trouble
        // implementing more important features like putting points on top by
        // default and integrating sketchclass layers with the z-order stacking
        // controls
        // if (
        //   a.renderUnder === RenderUnderType.Labels &&
        //   b.renderUnder !== RenderUnderType.Labels
        // ) {
        //   return 1;
        // } else if (
        //   b.renderUnder === RenderUnderType.Labels &&
        //   a.renderUnder !== RenderUnderType.Labels
        // ) {
        //   return -1;
        // } else
        if (
          // comparing two sublayers under the same datasource
          !isSketchClassLayerState(a) &&
          !isSketchClassLayerState(b) &&
          a.dataSourceId === b.dataSourceId
        ) {
          return getZIndex(a, true) - getZIndex(b, true);
        } else if (
          getZIndexOverride(a) === undefined &&
          getZIndexOverride(b) === undefined
        ) {
          // if neither layer has a zOrderOverride, sort by whether they are point layers
          const aIsPointLayer = isPointLayer(
            isSketchClassLayerState(a) ? a.styles : a.mapboxGlStyles
          );
          const bIsPointLayer = isPointLayer(
            isSketchClassLayerState(b) ? b.styles : b.mapboxGlStyles
          );
          // always sort point layers above non-point layers
          if (aIsPointLayer && !bIsPointLayer) {
            return -1;
          } else if (!aIsPointLayer && bIsPointLayer) {
            return 1;
          } else {
            // if both are point layers or both are non-point layers, sort by zIndex
            return getZIndex(a) - getZIndex(b);
          }
        } else {
          return getZIndex(a) - getZIndex(b);
        }
      })
      .map((l) => {
        const isSketchClass = isSketchClassLayerState(l);
        return {
          id: isSketchClass ? l.sketchClassId.toString() : l.tocId,
          finalZIndex: getZIndex(l),
          originalZIndex: "zIndex" in l ? l.zIndex : 0,
          zOrderOverride: getZIndexOverride(l),
          isPointLayer: isPointLayer(
            isSketchClass ? l.styles : l.mapboxGlStyles
          ),
          dataLayer: isSketchClass ? undefined : { ...l },
          sketchClassLayerState: isSketchClass ? l : undefined,
        };
      });
  }

  private getCurrentZOrder() {
    return this.getVisibleLayersByZIndex().map((layer) => {
      return {
        id: layer.id,
        label: layer.sketchClassLayerState
          ? layer.sketchClassLayerState.legendItem.label
          : this.tocItems[layer.dataLayer?.tocId || ""]?.label || "",
        zIndex: layer.finalZIndex,
        zOrderOverride: layer.zOrderOverride,
        originalZIndex: layer.originalZIndex,
      };
    });
  }

  moveLayerToTop(stableId: string, isSketchClassLayer?: boolean) {
    const currentOrder = this.getCurrentZOrder();
    if (currentOrder.length > 1) {
      const top = currentOrder[0];
      if (top.id !== stableId) {
        this.setZOrderOverride(stableId, top.zIndex - 1, isSketchClassLayer);
      }
    }
  }

  moveLayerToBottom(stableId: string, isSketchClassLayer?: boolean) {
    const currentOrder = this.getCurrentZOrder();
    if (currentOrder.length > 1) {
      const bottom = currentOrder[currentOrder.length - 1];
      if (bottom.id !== stableId) {
        this.setZOrderOverride(stableId, bottom.zIndex + 1, isSketchClassLayer);
      }
    }
  }

  setLayerOpacity(stableId: string, opacity: number) {
    if (opacity > 1 || opacity < 0) {
      throw new Error("Opacity should be between 0 and 1");
    }
    this.overlayStates.setOpacity(stableId, opacity);
    // Clock-hidden layers stay at paint 0 so the TOC slider cannot unhide them.
    // Stored LayerState.opacity is still updated for the next matching year.
    if (this.temporallyHiddenTocIds.has(stableId)) {
      return;
    }
    const layer = this.layers[stableId];
    if (!layer || !this.map) {
      this.debouncedUpdateStyle();
      return;
    }
    const source = this.clientDataSources[layer.dataSourceId];
    if (!source) {
      this.debouncedUpdateStyle();
      return;
    }
    const shouldHaveSourceLayer =
      source.type === DataSourceTypes.SeasketchMvt ||
      source.type === DataSourceTypes.Vector; // ||
    // source.type === DataSourceTypes.SeasketchRaster;
    const dataTableActivation =
      this.resolveDataTableVisualizationSettings(stableId);
    const dataTableActive = Boolean(
      dataTableActivation?.query.column && dataTableActivation?.query.op
    );
    if (dataTableActive) {
      // Must use the same layer id/paint as getComputedStyle. The old
      // dataTableCircleLayer helper used a `-data-table` id suffix, so
      // setPaintProperty was a no-op on a layer that isn't on the map.
      const dataTableLayer = this.buildDataTableProportionalSymbolLayer(
        layer,
        this.overlayStates.prefixedSourceId(layer.dataSourceId),
        shouldHaveSourceLayer ? layer.sourceLayer || undefined : undefined
      );
      if (this.map.getLayer(dataTableLayer.id)) {
        adjustLayerOpacities([dataTableLayer], opacity, this.map);
      } else {
        this.debouncedUpdateStyle();
      }
    } else if (layer.mapboxGlStyles?.length > 0) {
      let glLayers = (layer.mapboxGlStyles as any[]).map((lyr, i) => {
        return {
          ...lyr,
          source: this.overlayStates.prefixedSourceId(source.id),
          id: idForLayer(layer, i),
          ...(shouldHaveSourceLayer
            ? { "source-layer": layer.sourceLayer }
            : {}),
        } as AnyLayer;
      });
      adjustLayerOpacities(glLayers, opacity, this.map);
    } else {
      // Otherwise, update the whole map style
      // TODO: could make this more optimal for custom sources
      this.debouncedUpdateStyle();
    }
  }

  /**
   * Sets the opacity of a sketch class's layers
   */
  setSketchClassOpacity(sketchClassId: number, opacity: number) {
    const classId = sketchClassId.toString();
    if (!this.internalState.sketchClassLayerStates[classId]) {
      throw new Error(`Sketch class with id ${sketchClassId} does not exist`);
    } else {
      this.internalState.sketchClassLayerStates[classId].opacity = opacity;
    }

    // see if you can do an update of layers in place
    for (const sketchId of this.sketchStates.keys()) {
      const sketchLayerState = this.sketchStates.getRaw(sketchId);
      if (!sketchLayerState) continue;
      const layers = this.map?.getStyle().layers;
      if (layers?.length) {
        if (
          sketchLayerState.sketchClassId === sketchClassId &&
          sketchLayerState.visible
        ) {
          let glLayers = this.getLayersForSketch(
            parseInt(sketchId),
            false,
            sketchClassId
          );
          if (glLayers.length > 0) {
            adjustLayerOpacities(glLayers, opacity, this.map);
          }
        }
      }
    }

    this.pushSketchState();
    this.debouncedUpdatePreferences();

    // this.debouncedUpdateStyle();
  }

  setSketchClassHidden(sketchClassId: number, hidden: boolean) {
    const classId = sketchClassId.toString();
    if (!this.internalState.sketchClassLayerStates[classId]) {
      throw new Error(`Sketch class with id ${sketchClassId} does not exist`);
    } else {
      this.internalState.sketchClassLayerStates[classId].hidden = hidden;
    }

    this.pushSketchState();

    this.debouncedUpdateStyle();
  }

  setSketchClassZOrder(sketchClassId: number, zOrder: number) {
    const classId = sketchClassId.toString();
    if (!this.internalState.sketchClassLayerStates[classId]) {
      throw new Error(`Sketch class with id ${sketchClassId} does not exist`);
    } else {
      this.internalState.sketchClassLayerStates[classId].zOrderOverride =
        zOrder;
    }

    this.pushSketchState();
    this.debouncedUpdatePreferences();
    this.debouncedUpdateStyle();
  }

  private boundsForTocItem = async (stableId: string) => {
    let bounds: [number, number, number, number] | undefined;
    const item = this.tocItems[stableId];
    if (item) {
      if (
        (this.archivedSource &&
          item.dataLayerId === this.archivedSource.dataLayerId &&
          this.archivedSource.bounds) ||
        this.archivedSource?.dataSource?.bounds
      ) {
        bounds =
          ((this.archivedSource.bounds ||
            this.archivedSource?.dataSource?.bounds) as [
            number,
            number,
            number,
            number
          ]) || undefined;
      } else if (item.bounds) {
        bounds = item.bounds.map((coord: string | number) =>
          typeof coord === "string" ? parseFloat(coord) : coord
        ) as [number, number, number, number];
      } else if (item.isFolder) {
        bounds = createBoundsRecursive(item, Object.values(this.tocItems));
      } else {
        const layer = this.layers[stableId];
        const source = this.clientDataSources[layer?.dataSourceId];
        if (source && sourceTypeIsCustomGLSource(source.type)) {
          const existingSource = this.getCustomGLSource(source.id);
          if (existingSource) {
            const metadata = await existingSource?.getComputedMetadata();
            if (metadata?.bounds) {
              bounds = metadata.bounds;
            }
          } else {
            const customSource = createCustomSource(
              source,
              this.arcgisRequestManager,
              this.overlayStates.prefixedSourceId(source.id)
            );
            const metadata = await customSource.getComputedMetadata();
            if (metadata?.bounds) {
              bounds = metadata.bounds;
            }
            customSource.destroy();
          }
        }
      }
    }
    return bounds;
  };

  async zoomToTocItem(
    stableId: string,
    options?: { onlyIfNotVisible: boolean }
  ) {
    const bounds = await this.boundsForTocItem(stableId);
    if (options?.onlyIfNotVisible && bounds) {
      // Check the current map bounds. If any part of the bounds is visible, don't zoom
      const mapBounds = this.map?.getBounds()?.toArray().flat() as [
        number,
        number,
        number,
        number
      ];
      if (mapBounds) {
        const intersects = boundsIntersect(mapBounds, bounds);
        if (intersects) {
          return;
        }
      }
    }
    if (bounds && [180.0, 90.0, -180.0, -90.0].join(",") !== bounds.join(",")) {
      const sidebar = currentSidebarState();
      try {
        this.map?.fitBounds(bounds, {
          animate: true,
          padding: {
            bottom: 100,
            top: 100,
            left: sidebar.open ? sidebar.width + 100 : 100,
            right: 100,
          },
        });
      } catch (e) {
        // may be invalid bounds
        console.error(e);
      }
    }
  }

  private archivedSource: ArchivedSourceFragment | null = null;

  showArchivedSource(archive: ArchivedSourceFragment) {
    this.archivedSource = archive;
    this.debouncedUpdateStyle();
  }

  clearArchivedSource() {
    this.archivedSource = null;
    this.debouncedUpdateStyle();
  }
}

export default MapContextManager;

export type DataTableTooltipStatus = "loading" | "empty" | "value";

/** Structured hover payload for data-table proportional symbols. */
export type DataTableTooltipContent = {
  status: DataTableTooltipStatus;
  columnLabel: string;
  op: string;
  formattedValue?: string;
  tableName?: string;
  layerTitle?: string;
  siteLabel?: string;
  series?: DataTableFeatureSeriesPoint[];
  currentSteps?: string[];
  rangeLabel?: string;
};

export interface Tooltip {
  x: number;
  y: number;
  messages: string[];
  dataTable?: DataTableTooltipContent;
}

/**
 * Stable context for the MapContextManager instance, ready state, and
 * container portal. These values are set once (or very rarely) and almost
 * never change after initialisation, making this context safe to consume
 * without worrying about re-renders from map interaction.
 */
export interface MapManagerState {
  manager?: MapContextManager;
  /** Indicates the map state is ready to render a map */
  ready: boolean;
  containerPortal: HTMLDivElement | null;
  /** Populated when a basemap style fails to load */
  styleError?: Error;
}

export interface SketchLayerContextState {
  sketchLayerStates: { [id: number]: SketchLayerState };
  sketchClassLayerStates: { [sketchClassId: string]: SketchClassLayerState };
}

/** @deprecated Import from ./BasemapContext instead */
export type {
  BasemapContextState,
  OptionalBasemapLayerValue,
} from "./BasemapContext";
export { BasemapContext } from "./BasemapContext";

/** UI-driven state: tooltips, banners, popups, digitizing locks, etc. */
export interface MapUIStateContextState {
  bannerMessages: string[];
  tooltip?: Tooltip;
  fixedBlocks: string[];
  sidebarPopupContent?: string;
  sidebarPopupTitle?: string;
  /** TOC stableId of the layer shown in the sidebar popup, for the Data Tables footer */
  sidebarPopupTocStableId?: string;
  /** Mount point + layer for the Data Tables CTA portaled into the open map popup */
  dataTablesPopupTarget?: {
    element: HTMLElement;
    tocStableId: string;
    featureProperties?: { [name: string]: any };
  };
  displayedMapBookmark?: {
    id: string;
    errors: {
      missingLayers: string[];
      missingBasemap: boolean;
      missingSketches: number[];
    };
    supportsUndo: boolean;
  };
  loadingOverlay?: string | null;
  showLoadingOverlay?: boolean;
  offlineTileSimulatorActive?: boolean;
  digitizingLockState: DigitizingLockState;
  digitizingLockedBy?: string;
  showScale?: boolean;
  showCoordinates?: boolean;
  showFPS?: boolean;
  toggleScale: (show: boolean) => void;
  toggleCoordinates: (show: boolean) => void;
  toggleFPS: (show: boolean) => void;
  inaturalistCallToActions?: {
    projectId: string;
    label?: string;
  }[];
  languageCode?: string;
  /** Exposed for SketchUIStateContextProvider, SketchEditorModal, MapboxMap */
  interactivityManager?: LayerInteractivityManager;
}

/** Legend entries derived from visible layers. */
export interface LegendsContextState {
  legends: { [layerId: string]: LegendItem | null };
}

/** Layer visibility/loading state and style hash – the hot-path context. */
export interface MapOverlayContextState {
  layerStatesByTocStaticId: { [id: string]: LayerState };
  styleHash: string;
  /** Overlay data from useMapData - exposed for component consumption */
  dataLayers?: DataLayerDetailsFragment[];
  dataSources?: DataSourceDetailsFragment[];
  tableOfContentsItems?: OverlayFragment[];
}

/** @deprecated Use MapOverlayContextState */
export type LayerTreeContextState = MapOverlayContextState;

export interface MapContextInterface {
  layerStatesByTocStaticId: { [id: string]: LayerState };
  cameraOptions?: CameraOptions;
  offlineTileSimulatorActive?: boolean;
  styleHash: string;
  legends: {
    [layerId: string]: LegendItem | null;
  };
  digitizingLockState: DigitizingLockState;
  digitizingLockedBy?: string;
  showCoordinates?: boolean;
}
// useMapContext has been removed. Use MapManagerContextProvider + BasemapProvider instead.

/**
 * Context for sketch layer visibility and loading state.
 * Changes when sketches are toggled or loading status changes.
 */
export const SketchLayerContext = createContext<SketchLayerContextState>({
  sketchLayerStates: {},
  sketchClassLayerStates: {},
});

/**
 * A lightweight context that provides only the stable manager reference,
 * ready flag, and container portal. Consumers that only need to call
 * methods on the manager (and don't need reactive layer/UI state) should
 * prefer this context to avoid unnecessary re-renders.
 */
export const MapManagerContext = createContext<MapManagerState>({
  manager: undefined,
  ready: false,
  containerPortal: null,
});

/** Legend entries for visible layers. */
export const LegendsContext = createContext<LegendsContextState>({
  legends: {},
});

/** Layer visibility/loading and style hash – the hot-path context that changes on zoom/pan. */
export const MapOverlayContext = createContext<MapOverlayContextState>({
  layerStatesByTocStaticId: {},
  styleHash: "",
});

/** @deprecated Use MapOverlayContext */
export const LayerTreeContext = MapOverlayContext;

async function loadImage(url: string, map: mapboxgl.Map): Promise<any> {
  return new Promise((resolve, reject) => {
    map.loadImage(url, (error: Error | undefined, image: any) => {
      if (error) {
        reject(error);
      } else {
        resolve(image);
      }
    });
  });
}

/**
 * Generate an ID for a given layer in a ClientDataLayer. Note that SeaSketch DataLayers are not the same as Mapbox GL Style layers.
 * @param layer ClientDataLayer
 * @param styleLayerIndex ClientDataLayers' gl style contains multiple layers. You must specify the index of the layer to generate an ID
 */
export function idForLayer(
  layer: Pick<DataLayerDetailsFragment, "id" | "sublayer" | "dataSourceId">,
  styleLayerIndex?: number
) {
  if (layer.sublayer === null || layer.sublayer === undefined) {
    if (styleLayerIndex === undefined) {
      throw new Error(
        "styleLayerIndex must be provided to determine ID for a vector DataLayer"
      );
    } else {
      /* eslint-disable-next-line */
      return `seasketch/${layer.id}/${styleLayerIndex}`;
    }
  } else {
    return idForImageSource(layer.dataSourceId);
  }
}

/**
 * Extract the DataLayer id from a given GL Style layer's ID.
 * @param id
 */
export function layerIdFromStyleLayerId(id: string) {
  const re = /seasketch\/(\w+)\/\d+/;
  if (/seasketch\/\w+\/image/.test(id)) {
    throw new Error("Sublayer-type layer.");
  } else if (re.test(id)) {
    return re.exec(id)![1];
  } else {
    throw new Error("Does not appear to be a SeaSketch DataLayer. ID=" + id);
  }
}

function idForImageSource(sourceId: number | string) {
  /* eslint-disable-next-line */
  return `seasketch/${sourceId}/image`;
}

const layerIdRE = /^seasketch\//;
export function isSeaSketchLayerId(id: string) {
  return layerIdRE.test(id);
}

function reduceOpacity(layers: AnyLayer[], fraction = 0.5) {
  for (const layer of layers) {
    if ("paint" in layer) {
      switch (layer.type) {
        case "circle":
          reducePaintPropOpacity(layer, "circle-opacity", fraction);
          break;
        case "fill":
          reducePaintPropOpacity(layer, "fill-opacity", fraction);
          break;
        case "line":
          reducePaintPropOpacity(layer, "line-opacity", fraction);
          break;
        case "symbol":
          reducePaintPropOpacity(layer, "icon-opacity", fraction);
          reducePaintPropOpacity(layer, "text-opacity", fraction);
          break;
        default:
          break;
      }
    }
  }
}

function reducePaintPropOpacity(
  layer: any,
  propName: string,
  fraction: number
) {
  if ("paint" in layer) {
    const current = layer.paint[propName];
    if (typeof current === "number") {
      layer.paint[propName] = current * fraction;
    } else if (isExpression(current)) {
      layer.paint[propName] = hasZoomExpression(current)
        ? fraction
        : ["*", fraction, current];
    } else {
      layer.paint[propName] = fraction;
    }
  }
}

function sketchGeoJSONUrl(id: number, timestamp?: string | number) {
  return `${
    BASE_SERVER_ENDPOINT +
    // eslint-disable-next-line i18next/no-literal-string
    `/sketches/${id}.geojson.json?${
      // eslint-disable-next-line i18next/no-literal-string
      timestamp ? `?timestamp=${timestamp}` : ""
    }`
  }`;
}

export function sourceTypeIsCustomGLSource(type: DataSourceTypes) {
  return (
    type === DataSourceTypes.ArcgisVector ||
    type === DataSourceTypes.ArcgisRasterTiles ||
    type === DataSourceTypes.ArcgisDynamicMapserver
  );
}

type CustomSourceType =
  | DataSourceTypes.ArcgisVector
  | DataSourceTypes.ArcgisRasterTiles
  | DataSourceTypes.ArcgisDynamicMapserver;

function isCustomSourceType(type: DataSourceTypes): type is CustomSourceType {
  return sourceTypeIsCustomGLSource(type);
}

function createCustomSource(
  source: DataSourceDetailsFragment,
  requestManager: ArcGISRESTServiceRequestManager,
  sourceId: string
) {
  if (!isCustomSourceType(source.type)) {
    throw new Error("Source type is not custom");
  } else {
    switch (source.type) {
      case DataSourceTypes.ArcgisVector:
        const fetchStrategy =
          source.arcgisFetchStrategy === ArcgisFeatureLayerFetchStrategy.Raw
            ? "raw"
            : source.arcgisFetchStrategy ===
              ArcgisFeatureLayerFetchStrategy.Tiled
            ? "tiled"
            : "auto";
        return new ArcGISFeatureLayerSource(requestManager, {
          url: source.url!,
          fetchStrategy,
          sourceId,
          attributionOverride:
            (source.attribution || "").trim().length > 0
              ? source.attribution!
              : undefined,
        });
      case DataSourceTypes.ArcgisRasterTiles:
        return new ArcGISTiledMapService(requestManager, {
          url: source.url!,
          sourceId,
          attributionOverride:
            (source.attribution || "").trim().length > 0
              ? source.attribution!
              : undefined,
          maxZoom: source.maxzoom ? source.maxzoom : undefined,
          developerApiKey: process.env.REACT_APP_ARCGIS_DEVELOPER_API_KEY,
        });
      case DataSourceTypes.ArcgisDynamicMapserver:
        return new ArcGISDynamicMapService(requestManager, {
          url: source.url!,
          sourceId,
          supportHighDpiDisplays: source.useDevicePixelRatio || false,
          queryParameters: source.queryParameters || {},
          attributionOverride:
            (source.attribution || "").trim().length > 0
              ? source.attribution!
              : undefined,
        });
      default:
        throw new Error("Source type is not custom");
    }
  }
}

export function adjustLayerOpacities(
  layers: AnyLayer[],
  opacity: number,
  map?: Map
) {
  return layers.map((l) => {
    const layer = { ...l };
    if ("paint" in layer) {
      switch (layer.type) {
        case "circle":
          adjustPaintOpacityProp(layer, "circle-opacity", opacity, map);
          adjustPaintOpacityProp(layer, "circle-stroke-opacity", opacity, map);
          break;
        case "fill":
          adjustPaintOpacityProp(layer, "fill-opacity", opacity, map);
          break;
        case "line":
          adjustPaintOpacityProp(layer, "line-opacity", opacity, map);
          break;
        case "symbol":
          adjustPaintOpacityProp(layer, "icon-opacity", opacity, map);
          adjustPaintOpacityProp(layer, "text-opacity", opacity, map);
          break;
        case "heatmap":
          adjustPaintOpacityProp(layer, "heatmap-opacity", opacity, map);
          break;
        case "raster":
          adjustPaintOpacityProp(layer, "raster-opacity", opacity, map);
          break;
        default:
          break;
      }
    }
    return layer;
  });
}

function adjustPaintOpacityProp(
  layer: AnyLayer,
  propName: string,
  opacity: number,
  map?: Map
) {
  if ("paint" in layer) {
    const currentValue = (layer.paint as any)[propName];
    let value: number | Expression = opacity;
    if (typeof currentValue === "number") {
      value = ["*", opacity, currentValue];
    } else if (isExpression(currentValue)) {
      // check to make sure it's not a zoom expression. For some reason these
      // cause mapbox-gl to stop updating the style if combined with a
      // multiplication expression.
      if (hasZoomExpression(currentValue)) {
        value = opacity;
      } else {
        value = ["*", opacity, currentValue];
      }
    }
    layer.paint = {
      ...layer.paint,
      [propName]: opacity === 1 && currentValue ? currentValue : value,
    };
    if (map) {
      if (opacity === 1 && currentValue) {
        map.setPaintProperty(layer.id, propName, currentValue);
      } else {
        map.setPaintProperty(layer.id, propName, value);
      }
    }
  }
}

function hasZoomExpression(expression: Expression) {
  const fnName = expression[0];
  if (fnName === "zoom") {
    return true;
  } else {
    for (const arg of expression.slice(1)) {
      if (isExpression(arg)) {
        if (hasZoomExpression(arg)) {
          return true;
        }
      }
    }
  }
  return false;
}

// Calculates whether the given geographic extents cross, overlap, or contain each other
function boundsIntersect(
  mapBounds: [number, number, number, number],
  bounds: [number, number, number, number]
) {
  const [minX, minY, maxX, maxY] = mapBounds;
  const [minX2, minY2, maxX2, maxY2] = bounds;
  return minX <= maxX2 && maxX >= minX2 && minY <= maxY2 && maxY >= minY2;
}

function isPointLayer(mapboxGlStyles?: any): boolean {
  if (!mapboxGlStyles || mapboxGlStyles.length === 0) {
    return false;
  }
  return mapboxGlStyles.some(
    (l: any) => l.type === "circle" || l.type === "symbol"
  );
}

export function isSketchClassLayerState(
  layer: any
): layer is SketchClassLayerState {
  return layer && "sketchClassId" in layer;
}
